import { useCallback, useEffect, useRef } from 'react'
import {
  GrainPlayer,
  ToneAudioBuffer,
  Offline as ToneOffline,
  getContext as toneGetContext,
} from 'tone'
import {
  createRubberBandNode,
  warmRubberBandWorklet,
  type RubberBandNode,
} from '@/lib/rubberbandWorklet'
import { OutputSink } from '@/audio/OutputSink'
import {
  useAudioDevicesStore,
  refreshAudioDevices,
  ensureNativeDeviceChangeSubscription,
} from '@/stores/audioDevicesStore'
import {
  ensureBusOutputRouter,
  ensureBusRegistry,
  registerMasterPluginBus,
  resolveBusInput,
} from '@/audio/busRegistry'
import { MASTER_BUS_ID } from '@busswerks/shared'
import { ensureAudioInput } from '@/audio/AudioInput'
import { ensureMultiInput } from '@/audio/MultiInput'
import {
  identityCurve,
  bitcrushCurve,
  buildMasterFilterFx,
  buildDelayFx,
  buildReverbFx,
  buildBitcrushFx,
  buildStutterFx,
  buildTapeStopFx,
} from '@/audio/effects'
import { hasNativeAudioPlugin, nativeSetOutputDevice } from '@/lib/nativeAudio'
import { dbToLinear } from '@/lib/sampleNormalize'
import {
  applyScramblerParams,
  type ScramblerParams,
} from '@/lib/scramblerRandomizer'
import type { StepCell, Envelope } from '@busswerks/shared'
import { registerToneContext } from '@/audio/tone/registerContext'
import { registerDeepSubInstrument } from '@/audio/tone/instruments/deepSub'
import { registerPluckPadInstrument } from '@/audio/tone/instruments/pluckPad'
import { registerDroneInstrument } from '@/audio/tone/instruments/drone'
import { registerBuiltinPlugins } from '@/audio/plugins/builtins'
import { getInstrument } from '@/audio/plugins/registry'
import type {
  InstrumentPresetValue,
  InstrumentVoiceHandle,
} from '@/audio/plugins/types'
import { registerBuiltinInstruments } from '@/audio/plugins/instruments'
import { registerToneEffects } from '@/audio/tone/toneEffects'
import { registerFftAnalyzer } from '@/audio/tone/fftAnalyzer'
import { registerLoFiEffect } from '@/audio/tone/loFiEffect'
import { registerLfoTremoloEffect } from '@/audio/tone/lfoEffect'
import { WAM_BUNDLE } from '@/audio/wam/bundle'
import { registerWams } from '@/audio/wam/registry'
import { Bus } from '@/audio/Bus'
import type { BusEffect } from '@busswerks/shared'

/**
 * Procedural audio engine for the sequencer.
 *
 * Every pad voice is synthesized from Web Audio primitives — no
 * sample files ship, which keeps bundle size small and sidesteps
 * licensing. Four kits exist (classic / warehouse / crate / prism),
 * each parameterizing the same eight voice types with different
 * synthesis values.
 *
 * Voices no longer connect to `ctx.destination` directly. Instead
 * they connect to the singleton master FX bus, so the six-effect
 * bank (filter / delay / reverb / bitcrush / stutter / tape stop)
 * sits persistently in the signal path and activates via
 * momentary AudioParam ramps.
 */

export type KitId = 'classic' | 'warehouse' | 'crate' | 'prism'

export type VoiceType =
  | 'kick'
  | 'snare'
  | 'hatClosed'
  | 'hatOpen'
  | 'clap'
  | 'tom'
  | 'ride'
  | 'cowbell'

/**
 * A single sound on the pad grid. Kits all share the eight voice
 * types so projects can switch kit mid-session without re-mapping
 * pads — the `type` field routes into the synth dispatcher; the
 * `midiNote` is emitted out to hardware when MIDI-out is on.
 */
export interface KitVoice {
  label: string
  type: VoiceType
  /**
   * MIDI note number for hardware output + for matching incoming
   * MIDI (pads triggered externally). Kits lay the 8 voices out as
   * a chromatic scale starting at C3 (48) so pad N emits note
   * 48+N — intuitive for controllers played as a keyboard and
   * consistent with Keys Mode's scale-by-index mapping. The GM
   * drum map is NOT used: drum-keyboard users remap via MIDI
   * Learn if they need GM alignment.
   */
  midiNote: number
}

/**
 * Bundled kits. `classic` is the free-tier default; the other three
 * (`warehouse`, `crate`, `prism`) are Pro+ gated via
 * `features.sequencerKits === 'all'`.
 *
 * All kits share the chromatic pad layout (C3, C#3, D3, … G3 for
 * pads 1..8). Only the voice `type` + label differs per kit — the
 * pad→note mapping is a product decision, not a kit-level one.
 */
export const KITS: Record<KitId, { label: string; voices: KitVoice[] }> = {
  classic: {
    label: 'Nate08',
    voices: [
      { label: 'Kick', type: 'kick', midiNote: 48 },
      { label: 'Snare', type: 'snare', midiNote: 49 },
      { label: 'HH Closed', type: 'hatClosed', midiNote: 50 },
      { label: 'HH Open', type: 'hatOpen', midiNote: 51 },
      { label: 'Clap', type: 'clap', midiNote: 52 },
      { label: 'Tom', type: 'tom', midiNote: 53 },
      { label: 'Ride', type: 'ride', midiNote: 54 },
      { label: 'Cowbell', type: 'cowbell', midiNote: 55 },
    ],
  },
  warehouse: {
    label: 'Warehouse',
    voices: [
      { label: 'Kick', type: 'kick', midiNote: 48 },
      { label: 'Snare', type: 'snare', midiNote: 49 },
      { label: 'HH Closed', type: 'hatClosed', midiNote: 50 },
      { label: 'HH Open', type: 'hatOpen', midiNote: 51 },
      { label: 'Clap', type: 'clap', midiNote: 52 },
      { label: 'Tom', type: 'tom', midiNote: 53 },
      { label: 'Ride', type: 'ride', midiNote: 54 },
      { label: 'Cowbell', type: 'cowbell', midiNote: 55 },
    ],
  },
  crate: {
    label: 'Crate',
    voices: [
      { label: 'Kick', type: 'kick', midiNote: 48 },
      { label: 'Snare', type: 'snare', midiNote: 49 },
      { label: 'HH Closed', type: 'hatClosed', midiNote: 50 },
      { label: 'HH Open', type: 'hatOpen', midiNote: 51 },
      { label: 'Clap', type: 'clap', midiNote: 52 },
      { label: 'Tom', type: 'tom', midiNote: 53 },
      { label: 'Ride', type: 'ride', midiNote: 54 },
      { label: 'Cowbell', type: 'cowbell', midiNote: 55 },
    ],
  },
  prism: {
    label: 'Prism',
    voices: [
      { label: 'Kick', type: 'kick', midiNote: 48 },
      { label: 'Snare', type: 'snare', midiNote: 49 },
      { label: 'HH Closed', type: 'hatClosed', midiNote: 50 },
      { label: 'HH Open', type: 'hatOpen', midiNote: 51 },
      { label: 'Clap', type: 'clap', midiNote: 52 },
      { label: 'Bell', type: 'tom', midiNote: 53 },
      { label: 'Chime', type: 'ride', midiNote: 54 },
      { label: 'Bell Hi', type: 'cowbell', midiNote: 55 },
    ],
  },
}

// ── FX bus types ────────────────────────────────────────────

export type FxId =
  | 'filter'
  | 'delay'
  | 'reverb'
  | 'bitcrush'
  | 'tapeStop'
  | 'stutter'

// ── AudioContext singleton ──────────────────────────────────

function getAudioContextCtor(): typeof AudioContext | null {
  if (typeof window === 'undefined') return null
  return (
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: typeof AudioContext })
      .webkitAudioContext ??
    null
  )
}

let sharedCtx: AudioContext | null = null
let sharedNoiseBuffer: AudioBuffer | null = null
let sharedBus: FxBus | null = null
let sharedSink: OutputSink | null = null
/** `true` once we've wired the device-store subscription that
 *  forwards output selection changes to the sink. One-time setup. */
let outputDeviceSubscribed = false

function ensureContext(): AudioContext | null {
  if (sharedCtx) {
    if (sharedCtx.state === 'suspended') sharedCtx.resume()
    return sharedCtx
  }
  const Ctor = getAudioContextCtor()
  if (!Ctor) return null
  sharedCtx = new Ctor()
  // Warm the Rubber Band worklet module + WASM compile as soon as
  // the context exists so the first BPM-conform hit doesn't pay
  // the ~100 ms cold-start cost. Non-blocking.
  warmRubberBandWorklet(sharedCtx)
  return sharedCtx
}

/**
 * Access the shared AudioContext from outside the hook. Returns
 * `null` if the engine hasn't been initialised yet. Intended for
 * helpers that need to schedule events on the same context the
 * sequencer / pads / samples already use (e.g. the Phase 7
 * count-in metronome). Do NOT use this to bypass the engine's
 * bus / sink routing for content that should pass through
 * effects.
 */
export function getSharedAudioContext(): AudioContext | null {
  return sharedCtx
}

// ── Rubber Band pool ─────────────────────────────────────────
// Phase 5b: per-hit conform-to-BPM playback needs an
// AudioWorkletNode. Creating one is async (WASM init handshake)
// and must happen before scheduleSample — otherwise the hit
// would be late. We maintain a pool of pre-warmed nodes so
// borrowing is synchronous; borrowed nodes return to the pool
// when the hit ends.
//
// Cap at 8 — that's double the 4-voice polyphony we expect on
// any one bank. Exceeding the pool falls back to playbackRate.
// Each node is ~250 KB of WASM memory + a ring buffer.
const RB_POOL_MAX = 8
const rbPool: RubberBandNode[] = []
let rbPoolWarming = false

/** Kick off background creation of pool nodes up to the cap. */
function warmRbPool(ctx: AudioContext) {
  if (rbPoolWarming) return
  rbPoolWarming = true
  void (async () => {
    try {
      while (rbPool.length < RB_POOL_MAX) {
        const node = await createRubberBandNode(ctx)
        await node.ready
        rbPool.push(node)
      }
    } catch {
      // Silent — fallback path handles missing pool.
    } finally {
      rbPoolWarming = false
    }
  })()
}

/** Grab a pre-warmed node from the pool, or null if empty. The
 *  caller is responsible for returning the node via `releaseRbNode`
 *  when the hit ends (wire to BufferSource.onended). */
function borrowRbNode(
  tempoRatio: number,
  pitchSemitones: number,
): RubberBandNode | null {
  const node = rbPool.pop()
  if (!node) return null
  node.tempoRatio.value = tempoRatio
  node.pitchSemitones.value = pitchSemitones
  node.resetEngine()
  return node
}

function releaseRbNode(node: RubberBandNode) {
  try {
    node.disconnect()
  } catch {
    /* node may already be disconnected */
  }
  if (rbPool.length < RB_POOL_MAX) {
    rbPool.push(node)
  }
}

/** Reusable 1-second mono white-noise buffer. */
function getNoiseBuffer(ctx: AudioContext): AudioBuffer {
  if (sharedNoiseBuffer && sharedNoiseBuffer.sampleRate === ctx.sampleRate) {
    return sharedNoiseBuffer
  }
  const length = ctx.sampleRate
  const buf = ctx.createBuffer(1, length, ctx.sampleRate)
  const data = buf.getChannelData(0)
  for (let i = 0; i < length; i++) data[i] = Math.random() * 2 - 1
  sharedNoiseBuffer = buf
  return buf
}

// ── Per-kit voice parameters ────────────────────────────────

interface KickParams {
  osc: OscillatorType
  pitchHi: number
  pitchLo: number
  decay: number
  clickLevel: number
}
interface SnareParams {
  noiseFilterType: BiquadFilterType
  noiseFreq: number
  noiseDecay: number
  toneOsc: OscillatorType
  toneFreq: number
  toneDecay: number
  toneLevel: number
}
interface HatParams {
  filterType: BiquadFilterType
  freq: number
  closedDecay: number
  openDecay: number
  metallic: boolean
}
interface ClapParams {
  filterType: BiquadFilterType
  freq: number
  decay: number
  bursts: number
}
interface TomParams {
  osc: OscillatorType
  pitchHi: number
  pitchLo: number
  decay: number
  fm?: { ratio: number; depth: number }
}
interface RideParams {
  freqs: number[]
  osc: OscillatorType
  noiseFilterFreq: number
  decay: number
}
interface CowbellParams {
  freqs: number[]
  osc: OscillatorType
  decay: number
  fm?: { ratio: number; depth: number }
}

interface KitParams {
  kick: KickParams
  snare: SnareParams
  hat: HatParams
  clap: ClapParams
  tom: TomParams
  ride: RideParams
  cowbell: CowbellParams
}

/**
 * Per-kit parameter tables. The synth dispatcher below reads from
 * here instead of hard-coding values, so adding a kit is a matter
 * of filling out one entry.
 */
const KIT_PARAMS: Record<KitId, KitParams> = {
  // 808 homage: deep sub kick, buzzy snare, tight hats, stacked squares on rides.
  classic: {
    kick: {
      osc: 'sine',
      pitchHi: 150,
      pitchLo: 40,
      decay: 0.55,
      clickLevel: 0,
    },
    snare: {
      noiseFilterType: 'highpass',
      noiseFreq: 1200,
      noiseDecay: 0.18,
      toneOsc: 'triangle',
      toneFreq: 180,
      toneDecay: 0.11,
      toneLevel: 0.5,
    },
    hat: {
      filterType: 'highpass',
      freq: 7000,
      closedDecay: 0.06,
      openDecay: 0.35,
      metallic: false,
    },
    clap: { filterType: 'bandpass', freq: 1500, decay: 0.28, bursts: 3 },
    tom: { osc: 'sine', pitchHi: 200, pitchLo: 80, decay: 0.4 },
    ride: {
      freqs: [440, 660, 880],
      osc: 'square',
      noiseFilterFreq: 9000,
      decay: 0.45,
    },
    cowbell: { freqs: [560, 845], osc: 'square', decay: 0.3 },
  },
  // 909 homage: thud-not-boom kick, sharper snare with tone on top,
  // brighter + longer hats, saws on the ride, higher cowbell.
  warehouse: {
    kick: {
      osc: 'triangle',
      pitchHi: 100,
      pitchLo: 55,
      decay: 0.35,
      clickLevel: 0.15,
    },
    snare: {
      noiseFilterType: 'highpass',
      noiseFreq: 2000,
      noiseDecay: 0.12,
      toneOsc: 'square',
      toneFreq: 250,
      toneDecay: 0.08,
      toneLevel: 0.35,
    },
    hat: {
      filterType: 'highpass',
      freq: 9000,
      closedDecay: 0.04,
      openDecay: 0.28,
      metallic: true,
    },
    clap: { filterType: 'bandpass', freq: 2000, decay: 0.22, bursts: 3 },
    tom: { osc: 'triangle', pitchHi: 220, pitchLo: 110, decay: 0.3 },
    ride: {
      freqs: [520, 780, 1040],
      osc: 'sawtooth',
      noiseFilterFreq: 10000,
      decay: 0.6,
    },
    cowbell: { freqs: [700, 1050], osc: 'square', decay: 0.25 },
  },
  // Crate (lo-fi): pitched-down everything, noise-forward, slow
  // decays. Mimics dusty sampled drums without actually sampling.
  crate: {
    kick: {
      osc: 'sawtooth',
      pitchHi: 90,
      pitchLo: 30,
      decay: 0.7,
      clickLevel: 0.1,
    },
    snare: {
      noiseFilterType: 'bandpass',
      noiseFreq: 800,
      noiseDecay: 0.22,
      toneOsc: 'triangle',
      toneFreq: 140,
      toneDecay: 0.14,
      toneLevel: 0.3,
    },
    hat: {
      filterType: 'bandpass',
      freq: 4000,
      closedDecay: 0.08,
      openDecay: 0.4,
      metallic: false,
    },
    clap: { filterType: 'bandpass', freq: 900, decay: 0.15, bursts: 1 },
    tom: { osc: 'sawtooth', pitchHi: 150, pitchLo: 60, decay: 0.5 },
    ride: {
      freqs: [330, 495],
      osc: 'triangle',
      noiseFilterFreq: 3500,
      decay: 0.55,
    },
    cowbell: { freqs: [420], osc: 'square', decay: 0.35 },
  },
  // Prism (FM): metallic bells, carrier+modulator pairs. The "drums"
  // here are more percussive tones than kick/snare — intentionally.
  prism: {
    kick: { osc: 'sine', pitchHi: 200, pitchLo: 80, decay: 0.5, clickLevel: 0 },
    snare: {
      noiseFilterType: 'bandpass',
      noiseFreq: 3000,
      noiseDecay: 0.1,
      toneOsc: 'sine',
      toneFreq: 300,
      toneDecay: 0.18,
      toneLevel: 0.7,
    },
    hat: {
      filterType: 'highpass',
      freq: 8000,
      closedDecay: 0.05,
      openDecay: 0.3,
      metallic: true,
    },
    clap: { filterType: 'highpass', freq: 3500, decay: 0.18, bursts: 2 },
    tom: {
      osc: 'sine',
      pitchHi: 250,
      pitchLo: 100,
      decay: 0.4,
      fm: { ratio: 1.5, depth: 180 },
    },
    ride: {
      freqs: [700, 1050],
      osc: 'sine',
      noiseFilterFreq: 11000,
      decay: 0.8,
    },
    cowbell: {
      freqs: [800],
      osc: 'sine',
      decay: 0.4,
      fm: { ratio: 1.4, depth: 240 },
    },
  },
}

// ── Voice synthesis ─────────────────────────────────────────

interface EnvelopeOptions {
  attack?: number
  decay: number
  peak?: number
}

function envelope(
  ctx: AudioContext,
  when: number,
  { attack = 0.001, decay, peak = 1 }: EnvelopeOptions,
): GainNode {
  const gain = ctx.createGain()
  gain.gain.setValueAtTime(0, when)
  gain.gain.linearRampToValueAtTime(peak, when + attack)
  gain.gain.exponentialRampToValueAtTime(0.0001, when + attack + decay)
  return gain
}

function triggerKick(
  ctx: AudioContext,
  dest: AudioNode,
  when: number,
  velocity: number,
  p: KickParams,
) {
  const osc = ctx.createOscillator()
  osc.type = p.osc
  osc.frequency.setValueAtTime(p.pitchHi, when)
  osc.frequency.exponentialRampToValueAtTime(p.pitchLo, when + p.decay)
  const gain = envelope(ctx, when, { decay: p.decay, peak: velocity })
  osc.connect(gain).connect(dest)
  osc.start(when)
  osc.stop(when + p.decay + 0.05)

  // Optional transient "click" — a 5ms noise burst that gives 909-
  // style kicks their attack.
  if (p.clickLevel > 0) {
    const click = ctx.createBufferSource()
    click.buffer = getNoiseBuffer(ctx)
    const clickHP = ctx.createBiquadFilter()
    clickHP.type = 'highpass'
    clickHP.frequency.value = 3000
    const clickGain = envelope(ctx, when, {
      decay: 0.015,
      peak: velocity * p.clickLevel,
    })
    click.connect(clickHP).connect(clickGain).connect(dest)
    click.start(when)
    click.stop(when + 0.03)
  }
}

function triggerSnare(
  ctx: AudioContext,
  dest: AudioNode,
  when: number,
  velocity: number,
  p: SnareParams,
) {
  const noise = ctx.createBufferSource()
  noise.buffer = getNoiseBuffer(ctx)
  const noiseFilter = ctx.createBiquadFilter()
  noiseFilter.type = p.noiseFilterType
  noiseFilter.frequency.value = p.noiseFreq
  const noiseGain = envelope(ctx, when, {
    decay: p.noiseDecay,
    peak: velocity * 0.9,
  })
  noise.connect(noiseFilter).connect(noiseGain).connect(dest)
  noise.start(when)
  noise.stop(when + p.noiseDecay + 0.02)

  const osc = ctx.createOscillator()
  osc.type = p.toneOsc
  osc.frequency.setValueAtTime(p.toneFreq, when)
  const oscGain = envelope(ctx, when, {
    decay: p.toneDecay,
    peak: velocity * p.toneLevel,
  })
  osc.connect(oscGain).connect(dest)
  osc.start(when)
  osc.stop(when + p.toneDecay + 0.03)
}

function triggerHat(
  ctx: AudioContext,
  dest: AudioNode,
  when: number,
  velocity: number,
  p: HatParams,
  open: boolean,
) {
  const noise = ctx.createBufferSource()
  noise.buffer = getNoiseBuffer(ctx)
  const filter = ctx.createBiquadFilter()
  filter.type = p.filterType
  filter.frequency.value = p.freq
  if (p.metallic) filter.Q.value = 3
  const decay = open ? p.openDecay : p.closedDecay
  const gain = envelope(ctx, when, { decay, peak: velocity * 0.6 })
  noise.connect(filter).connect(gain).connect(dest)
  noise.start(when)
  noise.stop(when + decay + 0.02)
}

function triggerClap(
  ctx: AudioContext,
  dest: AudioNode,
  when: number,
  velocity: number,
  p: ClapParams,
) {
  const noise = ctx.createBufferSource()
  noise.buffer = getNoiseBuffer(ctx)
  const filter = ctx.createBiquadFilter()
  filter.type = p.filterType
  filter.frequency.value = p.freq
  filter.Q.value = 0.8
  const g = ctx.createGain()
  g.gain.setValueAtTime(0, when)
  // Stacked short bursts approximate the classic hand-clap flam.
  for (let i = 0; i < p.bursts; i++) {
    g.gain.linearRampToValueAtTime(velocity * 0.8, when + 0.01 + i * 0.012)
    g.gain.linearRampToValueAtTime(0, when + 0.02 + i * 0.012)
  }
  g.gain.linearRampToValueAtTime(velocity * 0.5, when + 0.04)
  g.gain.exponentialRampToValueAtTime(0.0001, when + p.decay)
  noise.connect(filter).connect(g).connect(dest)
  noise.start(when)
  noise.stop(when + p.decay + 0.02)
}

function triggerTom(
  ctx: AudioContext,
  dest: AudioNode,
  when: number,
  velocity: number,
  p: TomParams,
) {
  const osc = ctx.createOscillator()
  osc.type = p.osc
  osc.frequency.setValueAtTime(p.pitchHi, when)
  osc.frequency.exponentialRampToValueAtTime(p.pitchLo, when + p.decay)
  const gain = envelope(ctx, when, { decay: p.decay, peak: velocity })

  // FM option: attach a modulator to the carrier's frequency param.
  if (p.fm) {
    const mod = ctx.createOscillator()
    mod.type = 'sine'
    mod.frequency.value = p.pitchHi * p.fm.ratio
    const modGain = ctx.createGain()
    modGain.gain.value = p.fm.depth
    mod.connect(modGain).connect(osc.frequency)
    mod.start(when)
    mod.stop(when + p.decay + 0.05)
  }

  osc.connect(gain).connect(dest)
  osc.start(when)
  osc.stop(when + p.decay + 0.05)
}

function triggerRide(
  ctx: AudioContext,
  dest: AudioNode,
  when: number,
  velocity: number,
  p: RideParams,
) {
  // Stacked detuned oscillators approximate the bell-ish overtone
  // structure. Real cymbals need many more partials; this is a
  // usable suggestion.
  p.freqs.forEach(f => {
    const osc = ctx.createOscillator()
    osc.type = p.osc
    osc.frequency.value = f
    const gain = envelope(ctx, when, { decay: p.decay, peak: velocity * 0.06 })
    osc.connect(gain).connect(dest)
    osc.start(when)
    osc.stop(when + p.decay + 0.05)
  })
  const noise = ctx.createBufferSource()
  noise.buffer = getNoiseBuffer(ctx)
  const filter = ctx.createBiquadFilter()
  filter.type = 'highpass'
  filter.frequency.value = p.noiseFilterFreq
  const gain = envelope(ctx, when, { decay: p.decay, peak: velocity * 0.2 })
  noise.connect(filter).connect(gain).connect(dest)
  noise.start(when)
  noise.stop(when + p.decay + 0.05)
}

function triggerCowbell(
  ctx: AudioContext,
  dest: AudioNode,
  when: number,
  velocity: number,
  p: CowbellParams,
) {
  const g = ctx.createGain()
  g.gain.setValueAtTime(0, when)
  g.gain.linearRampToValueAtTime(velocity * 0.6, when + 0.002)
  g.gain.exponentialRampToValueAtTime(0.0001, when + p.decay)
  g.connect(dest)
  p.freqs.forEach(f => {
    const osc = ctx.createOscillator()
    osc.type = p.osc
    osc.frequency.value = f
    if (p.fm) {
      const mod = ctx.createOscillator()
      mod.type = 'sine'
      mod.frequency.value = f * p.fm.ratio
      const modGain = ctx.createGain()
      modGain.gain.value = p.fm.depth
      mod.connect(modGain).connect(osc.frequency)
      mod.start(when)
      mod.stop(when + p.decay + 0.05)
    }
    osc.connect(g)
    osc.start(when)
    osc.stop(when + p.decay + 0.02)
  })
}

/**
 * Scale a set of kit parameters' frequency-like fields by a pitch
 * ratio. Rather than threading a `pitchRatio` arg through every
 * inner trigger signature, we produce a one-off parameter object
 * with multiplied frequencies and let each voice render normally.
 *
 * `semitonesToRatio(0) === 1`, so the zero-pitch path produces the
 * same object-shape allocation but no audible change. Cheap enough
 * to do on every trigger.
 */
function semitonesToRatio(semitones: number): number {
  return Math.pow(2, semitones / 12)
}

function pitchKick(p: KickParams, ratio: number): KickParams {
  if (ratio === 1) return p
  return { ...p, pitchHi: p.pitchHi * ratio, pitchLo: p.pitchLo * ratio }
}
function pitchSnare(p: SnareParams, ratio: number): SnareParams {
  if (ratio === 1) return p
  return { ...p, toneFreq: p.toneFreq * ratio, noiseFreq: p.noiseFreq * ratio }
}
function pitchHat(p: HatParams, ratio: number): HatParams {
  if (ratio === 1) return p
  return { ...p, freq: p.freq * ratio }
}
function pitchClap(p: ClapParams, ratio: number): ClapParams {
  if (ratio === 1) return p
  return { ...p, freq: p.freq * ratio }
}
function pitchTom(p: TomParams, ratio: number): TomParams {
  if (ratio === 1) return p
  return {
    ...p,
    pitchHi: p.pitchHi * ratio,
    pitchLo: p.pitchLo * ratio,
    fm: p.fm ? { ratio: p.fm.ratio, depth: p.fm.depth * ratio } : undefined,
  }
}
function pitchRide(p: RideParams, ratio: number): RideParams {
  if (ratio === 1) return p
  return {
    ...p,
    freqs: p.freqs.map(f => f * ratio),
    noiseFilterFreq: p.noiseFilterFreq * ratio,
  }
}
function pitchCowbell(p: CowbellParams, ratio: number): CowbellParams {
  if (ratio === 1) return p
  return { ...p, freqs: p.freqs.map(f => f * ratio) }
}

/**
 * Dispatcher — turn a procedural voice type + kit into actual nodes.
 * `when` is in AudioContext time; `velocity` is a 0..1 gain scale;
 * `pitchSemitones` transposes every frequency-like kit param.
 */
/**
 * Wrap `dest` with an ADSR gain overlay for procedural voices. The
 * voice connects to the returned node instead of `dest`; we ramp
 * the wrapper's gain through the ADSR shape (attack → decay →
 * sustain while `gateSec` lasts → release) before reconnecting
 * to `dest`. Multiplies on top of the voice's internal envelope —
 * sustain=1 with near-zero A/R leaves voice character intact,
 * which is the default shape.
 */
function wrapWithEnvelope(
  ctx: AudioContext,
  dest: AudioNode,
  when: number,
  envelope: Partial<SampleEnvelope>,
  gateSec: number,
): GainNode {
  const env: SampleEnvelope = {
    a: envelope.a ?? DEFAULT_ENVELOPE.a,
    d: envelope.d ?? DEFAULT_ENVELOPE.d,
    s: envelope.s ?? DEFAULT_ENVELOPE.s,
    r: envelope.r ?? DEFAULT_ENVELOPE.r,
  }
  const gain = ctx.createGain()
  const peakT = when + Math.max(0, env.a)
  const sustainT = peakT + Math.max(0, env.d)
  const gate = Math.max(0, gateSec)
  const releaseT = Math.max(sustainT, when + gate)
  const endT = releaseT + Math.max(0, env.r)
  gain.gain.setValueAtTime(0, when)
  gain.gain.linearRampToValueAtTime(1, peakT)
  gain.gain.linearRampToValueAtTime(env.s, sustainT)
  gain.gain.setValueAtTime(env.s, releaseT)
  gain.gain.linearRampToValueAtTime(0, endT)
  gain.connect(dest)
  return gain
}

function triggerVoice(
  ctx: AudioContext,
  dest: AudioNode,
  kitId: KitId,
  type: VoiceType,
  when: number,
  velocity: number,
  pitchSemitones: number,
  envelope?: Partial<SampleEnvelope>,
  gateSec?: number,
) {
  const kp = KIT_PARAMS[kitId]
  const r = semitonesToRatio(pitchSemitones)
  // If the caller handed us an envelope, insert a gain overlay
  // between the voice and `dest`. Everything below uses `voiceDest`
  // in place of `dest`, so the voice's final connection ends at
  // the ADSR wrapper which then routes into the original target.
  const voiceDest =
    envelope !== undefined
      ? wrapWithEnvelope(ctx, dest, when, envelope, gateSec ?? 0.12)
      : dest
  switch (type) {
    case 'kick':
      return triggerKick(ctx, voiceDest, when, velocity, pitchKick(kp.kick, r))
    case 'snare':
      return triggerSnare(
        ctx,
        voiceDest,
        when,
        velocity,
        pitchSnare(kp.snare, r),
      )
    case 'hatClosed':
      return triggerHat(
        ctx,
        voiceDest,
        when,
        velocity,
        pitchHat(kp.hat, r),
        false,
      )
    case 'hatOpen':
      return triggerHat(
        ctx,
        voiceDest,
        when,
        velocity,
        pitchHat(kp.hat, r),
        true,
      )
    case 'clap':
      return triggerClap(ctx, voiceDest, when, velocity, pitchClap(kp.clap, r))
    case 'tom':
      return triggerTom(ctx, voiceDest, when, velocity, pitchTom(kp.tom, r))
    case 'ride':
      return triggerRide(ctx, voiceDest, when, velocity, pitchRide(kp.ride, r))
    case 'cowbell':
      return triggerCowbell(
        ctx,
        voiceDest,
        when,
        velocity,
        pitchCowbell(kp.cowbell, r),
      )
  }
}

// ── Master FX bus ───────────────────────────────────────────

interface FxBus {
  /** Every voice connects here instead of ctx.destination. */
  input: GainNode
  /**
   * Ramp AudioParams / swap curves to engage or bypass an FX. `amount`
   * (0..1) scales the engaged target so the FX Bank faders can dial in
   * the intensity; `amount === 1` reproduces the original binary-bank
   * behavior. `tapeStop` ignores `amount` (it's a binary ramp).
   */
  engage: (fxId: FxId, active: boolean, amount?: number) => void
  /**
   * Per-step FX automation — momentarily bump an FX's wet level at
   * `when` and ramp back toward the bank's resting level over
   * `holdSec`. Used by the scheduler to apply Tracker-authored
   * per-step FX sends without re-wiring voices. Pass an explicit
   * `restLevel` to override the bus's tracked resting value (rare —
   * normally the bus already has it right).
   */
  /**
   * Performance-FX filter mode. 1 = highpass, 2 = lowpass. Flipping
   * the mode preserves the current cutoff sweep so a user drag across
   * the HP/LP switch doesn't snap the cutoff back to bypass.
   * `tapeStop` forces lowpass during its muffle ramp regardless of
   * this setting — documented below.
   */
  setFilterMode: (mode: 1 | 2) => void
  automateSend: (
    fxId: FxId,
    when: number,
    amount: number,
    holdSec: number,
    restLevel?: number,
  ) => void
}

// `createImpulseResponse`, `identityCurve`, and `bitcrushCurve`
// moved to `@/audio/effects` as part of the Phase 8a refactor so
// per-bus FX chains can reuse the same graph builders. Imported
// at the top of this file — behaviour is identical.

/** Target wet level when the named FX is engaged by the held bank. */
const FX_ENGAGED_LEVEL: Record<FxId, number> = {
  filter: 500, // cutoff Hz (lower = more effect)
  delay: 0.7,
  reverb: 0.55,
  bitcrush: 0.9,
  stutter: 0.95,
  tapeStop: 0, // tape-stop is binary; desired level not meaningful for send
}

function createFxBus(ctx: AudioContext, destination: AudioNode): FxBus {
  const input = ctx.createGain()

  // ── Serial: input → masterFilter → preFx split point ────
  // Master filter is a unipolar low-pass sweep owned by `engage`,
  // so we keep a direct reference via `masterFilterFx.filter`.
  const masterFilterFx = buildMasterFilterFx(ctx)
  const masterFilter = masterFilterFx.filter
  input.connect(masterFilterFx.input)

  const preFx = ctx.createGain()
  masterFilterFx.output.connect(preFx)

  // ── Dry tap ──
  const dryTap = ctx.createGain()
  dryTap.gain.value = 1
  preFx.connect(dryTap)

  // ── Parallel sends ──
  // Each builder produces an identical sub-graph to the old inline
  // construction; we just name the handles we still need for the
  // engage / automate logic below.
  const delayFx = buildDelayFx(ctx)
  preFx.connect(delayFx.input)
  // `delay` handle not used by engage(), but the destructure is
  // convenient documentation of what buildDelayFx exposes.
  const { feedback: delayFeedback, wet: delayWet } = delayFx

  const reverbFx = buildReverbFx(ctx)
  preFx.connect(reverbFx.input)
  const { wet: reverbWet } = reverbFx

  const crushFx = buildBitcrushFx(ctx)
  preFx.connect(crushFx.input)
  const { shaper: crushNode, wet: crushWet } = crushFx

  const stutterFx = buildStutterFx(ctx)
  preFx.connect(stutterFx.input)
  const { feedback: stutterFeedback, wet: stutterWet } = stutterFx

  // ── Mix sum ──
  const outMix = ctx.createGain()
  dryTap.connect(outMix)
  delayWet.connect(outMix)
  reverbWet.connect(outMix)
  crushWet.connect(outMix)
  stutterWet.connect(outMix)

  // ── Tape-stop master chain ──
  // Extracted builder — but the master's tape-stop couples with
  // `masterFilter` in `engage()`, which is unipolar-filter-specific
  // logic that stays inline below.
  const tapeFx = buildTapeStopFx(ctx)
  const { delay: tapeDelay, gain: tapeGain } = tapeFx
  // Route to caller-supplied destination (the OutputSink's input in
  // live playback; `ctx.destination` in OfflineAudioContext renders).
  // Preserves the in-chain tape-stop behavior unchanged.
  outMix.connect(tapeFx.input)
  tapeFx.output.connect(destination)

  // Performance-FX filter mode — flipped by setFilterMode, read in
  // engage('filter'). Captured in closure so each FxBus owns its own
  // mode state; live + offline render contexts don't share a global.
  let perfFilterMode: 1 | 2 = 2
  // Track filter engage state separately from the parallel-wet
  // restLevels table (filter is a serial effect; it doesn't live in
  // that table). Used by setFilterMode to re-apply the current engage
  // state against the new mode's cutoff range.
  let filterActive = false
  let filterAmount = 1

  /**
   * Ramp AudioParams to the engaged or bypassed state. Cancel any
   * pending ramps first so rapid hold/release on the UI doesn't
   * accumulate stacked schedules. `amount` (0..1) scales each effect's
   * engaged target so the bank's per-fx fader can dial intensity.
   * `tapeStop` ignores `amount` — it's a binary master ramp.
   */
  const engage = (fxId: FxId, active: boolean, amount = 1) => {
    const t = ctx.currentTime
    const ramp = 0.08
    const a = Math.max(0, Math.min(1, amount))
    switch (fxId) {
      case 'filter': {
        // Unipolar filter sweep. For lowpass (default / legacy):
        //   amount 0 = bypass (22 kHz cutoff), 1 = full muffle
        //   (500 Hz). Users reported the previous bipolar design "did
        //   nothing when enabled" — centre-tap landed on the bipolar
        //   bypass point. Unipolar makes any non-bottom tap audibly
        //   muffle, which matches the groovebox "filter cut" expect.
        // For highpass:
        //   amount 0 = bypass (20 Hz cutoff), 1 = full cut (2 kHz)
        //   which kills everything below the vocal range. Symmetric
        //   intent to LP — bottom of the drag = no effect, top of
        //   the drag = maximally-filtered signal.
        //
        // `tapeStop` still forces lowpass during its muffle ramp
        // regardless of perfFilterMode — documented in that case.
        filterActive = active
        filterAmount = a
        masterFilter.frequency.cancelScheduledValues(t)
        masterFilter.frequency.setValueAtTime(masterFilter.frequency.value, t)
        if (perfFilterMode === 1) {
          masterFilter.type = 'highpass'
          if (!active) {
            masterFilter.frequency.linearRampToValueAtTime(20, t + ramp)
          } else {
            const target = 20 + (2000 - 20) * a
            masterFilter.frequency.linearRampToValueAtTime(target, t + ramp)
          }
        } else {
          masterFilter.type = 'lowpass'
          if (!active) {
            masterFilter.frequency.linearRampToValueAtTime(22000, t + ramp)
          } else {
            const target = 22000 - (22000 - 500) * a
            masterFilter.frequency.linearRampToValueAtTime(target, t + ramp)
          }
        }
        break
      }
      case 'delay': {
        delayFeedback.gain.cancelScheduledValues(t)
        delayFeedback.gain.setValueAtTime(delayFeedback.gain.value, t)
        delayFeedback.gain.linearRampToValueAtTime(
          active ? 0.55 * a : 0,
          t + ramp,
        )
        delayWet.gain.cancelScheduledValues(t)
        delayWet.gain.setValueAtTime(delayWet.gain.value, t)
        delayWet.gain.linearRampToValueAtTime(active ? 0.7 * a : 0, t + ramp)
        break
      }
      case 'reverb': {
        reverbWet.gain.cancelScheduledValues(t)
        reverbWet.gain.setValueAtTime(reverbWet.gain.value, t)
        reverbWet.gain.linearRampToValueAtTime(active ? 0.55 * a : 0, t + ramp)
        break
      }
      case 'bitcrush': {
        crushNode.curve = active ? bitcrushCurve(4) : identityCurve()
        crushWet.gain.cancelScheduledValues(t)
        crushWet.gain.setValueAtTime(crushWet.gain.value, t)
        crushWet.gain.linearRampToValueAtTime(active ? 0.9 * a : 0, t + ramp)
        break
      }
      case 'stutter': {
        stutterFeedback.gain.cancelScheduledValues(t)
        stutterFeedback.gain.setValueAtTime(stutterFeedback.gain.value, t)
        stutterFeedback.gain.linearRampToValueAtTime(
          active ? 0.95 * a : 0,
          t + ramp,
        )
        stutterWet.gain.cancelScheduledValues(t)
        stutterWet.gain.setValueAtTime(stutterWet.gain.value, t)
        stutterWet.gain.linearRampToValueAtTime(active ? 0.95 * a : 0, t + ramp)
        break
      }
      case 'tapeStop': {
        const engageRamp = 0.6
        const releaseRamp = 0.15
        tapeGain.gain.cancelScheduledValues(t)
        tapeGain.gain.setValueAtTime(tapeGain.gain.value, t)
        tapeDelay.delayTime.cancelScheduledValues(t)
        tapeDelay.delayTime.setValueAtTime(tapeDelay.delayTime.value, t)
        masterFilter.frequency.cancelScheduledValues(t)
        masterFilter.frequency.setValueAtTime(masterFilter.frequency.value, t)
        masterFilter.Q.cancelScheduledValues(t)
        masterFilter.Q.setValueAtTime(masterFilter.Q.value, t)
        // Force 'lowpass' for tapeStop's muffle ramp — the filter fader
        // may have left the node in 'highpass' mode, which would turn
        // the muffle sweep into a high-pass cut that doesn't match the
        // intended tape-halt feel.
        masterFilter.type = 'lowpass'
        if (active) {
          // Three coordinated ramps give the effect its character:
          //   1. Delay-time up → downward pitch bend as the signal
          //      stretches (capstan losing tension).
          //   2. Filter Q up → resonance peak that sweeps with the
          //      cutoff, like the head losing HF contact.
          //   3. Exponential cutoff drop → perceptually-natural
          //      slowdown curve (linear sounds mechanical).
          tapeDelay.delayTime.linearRampToValueAtTime(0.08, t + engageRamp)
          masterFilter.Q.linearRampToValueAtTime(6, t + engageRamp * 0.5)
          masterFilter.frequency.exponentialRampToValueAtTime(
            120,
            t + engageRamp,
          )
          tapeGain.gain.linearRampToValueAtTime(0, t + engageRamp)
        } else {
          tapeDelay.delayTime.linearRampToValueAtTime(0, t + releaseRamp)
          masterFilter.Q.linearRampToValueAtTime(1, t + releaseRamp)
          masterFilter.frequency.exponentialRampToValueAtTime(
            22000,
            t + releaseRamp,
          )
          tapeGain.gain.linearRampToValueAtTime(1, t + releaseRamp)
        }
        break
      }
    }
  }

  // Track what the bank wants the wet level to rest at. Per-step
  // automation ramps away and then back to whichever resting level
  // the bank dictates. Updated inside engage() for the four parallel
  // wets; filter + tapeStop are binary serial effects and don't
  // participate in per-step sends (automateSend no-ops on them).
  const restLevels: Partial<Record<FxId, number>> = {
    delay: 0,
    reverb: 0,
    bitcrush: 0,
    stutter: 0,
  }

  // Wrap `engage` so the rest-level table stays in sync with the
  // bank state AND its current fader amount. Using this indirection
  // (vs. duplicating logic in engage) keeps the two in lockstep.
  const markRestLevel = (fxId: FxId, active: boolean, amount: number) => {
    if (fxId in restLevels) {
      restLevels[fxId] = active ? FX_ENGAGED_LEVEL[fxId] * amount : 0
    }
  }

  const publicEngage = (fxId: FxId, active: boolean, amount = 1) => {
    engage(fxId, active, amount)
    markRestLevel(fxId, active, amount)
  }

  const wetNodeForSend: Partial<Record<FxId, GainNode>> = {
    delay: delayWet,
    reverb: reverbWet,
    bitcrush: crushWet,
    stutter: stutterWet,
  }

  const automateSend: FxBus['automateSend'] = (
    fxId,
    when,
    amount,
    holdSec,
    restLevel,
  ) => {
    const target = wetNodeForSend[fxId]
    if (!target) return // filter / tapeStop: no per-step send
    const clamped = Math.max(0, Math.min(1, amount))
    const rest = Math.max(0, Math.min(1, restLevel ?? restLevels[fxId] ?? 0))
    const at = Math.max(when, ctx.currentTime)
    target.gain.cancelScheduledValues(at)
    // Seed the hold at the caller's `amount` right at the step time,
    // then ramp back to the bank's resting level over `holdSec` so
    // the FX tail decays naturally into subsequent steps.
    target.gain.setValueAtTime(clamped, at)
    target.gain.linearRampToValueAtTime(rest, at + Math.max(0.01, holdSec))

    // Bitcrush also needs its curve swapped to the step curve for
    // the duration. Easiest: swap to crush curve now, swap back if
    // the resting state is bypassed. This isn't sample-accurate
    // but matches the audible effect window.
    if (fxId === 'bitcrush') {
      if (crushNode.curve !== cachedBitcrushCurve) {
        crushNode.curve = cachedBitcrushCurve
      }
    }
  }

  /**
   * Flip the performance-FX filter between HP and LP. Re-runs the
   * filter's engage() with the current bank state so the cutoff
   * immediately re-targets to the new mode's sweep range instead of
   * snapping back to bypass.
   */
  const setFilterMode: FxBus['setFilterMode'] = mode => {
    perfFilterMode = mode
    // Re-apply the filter with the tracked active/amount so the cutoff
    // immediately re-targets to the new mode's sweep range. If the
    // filter is currently bypassed we still invoke engage(false) so
    // the mode flip sets type correctly even while bypassed (so a
    // subsequent "on" tap uses the right type).
    engage('filter', filterActive, filterAmount)
  }

  return {
    input,
    engage: publicEngage,
    setFilterMode,
    automateSend,
  }
}

// Cached curves so we don't allocate on every bitcrush send.
const cachedBitcrushCurve = bitcrushCurve(4)

/**
 * Lazily construct (and cache) the OutputSink for the live
 * AudioContext. Wires the first-time device-store subscription so
 * user picks in AudioPanel reroute the entire graph to the chosen
 * sink without tearing the audio graph down.
 *
 * When the sink can't switch outputs (Firefox, WKWebView), the
 * subscription still fires but `setDeviceId` is a no-op — the
 * store's `canSwitchOutput` flag tells the UI whether to even
 * show a device picker.
 */
/**
 * Apply the user's chosen output device via whichever route works
 * on the current runtime:
 *   - Native (Catalyst): call the native plugin, which writes
 *     `kAudioHardwarePropertyDefaultOutputDevice`. Affects the
 *     whole Mac — the UI labels it as a system change.
 *   - Web (setSinkId-capable): retarget the OutputSink so audio
 *     flows through the chosen sink per-tab.
 *   - Fallback: no-op (destination-only mode).
 * Silently ignores the `DEFAULT_OUTPUT_ID` sentinel on native —
 * "system default" means "no preference," so the plugin doesn't
 * need to fire to reset anything.
 */
async function applySelectedOutput(deviceId: string): Promise<void> {
  if (hasNativeAudioPlugin()) {
    if (!deviceId || deviceId === 'default') return
    await nativeSetOutputDevice(deviceId)
    return
  }
  await sharedSink?.setDeviceId(deviceId)
}

function ensureOutputSink(ctx: AudioContext): OutputSink {
  if (sharedSink) return sharedSink
  sharedSink = new OutputSink(ctx)
  // Apply the persisted selection immediately so reloads don't
  // silently revert to the system default after the user chose
  // another output last session.
  const initialId = useAudioDevicesStore.getState().selectedOutputId
  if (initialId) {
    void applySelectedOutput(initialId).catch(() => {
      /* id may be stale (device unplugged between sessions);
         sink stays on default until the user picks something new */
    })
  }
  if (!outputDeviceSubscribed) {
    outputDeviceSubscribed = true
    useAudioDevicesStore.subscribe((state, prev) => {
      if (state.selectedOutputId === prev.selectedOutputId) return
      void applySelectedOutput(state.selectedOutputId).catch(() => {
        /* surface to UI later; for now we fall back to the
           previously-active sink */
      })
    })
    // Populate the device list early so the AudioPanel has something
    // to render on first open. Also re-enumerate on hotplug. Fire-
    // and-forget — enumeration is fast and doesn't require a gesture.
    void refreshAudioDevices()
    if (typeof navigator !== 'undefined' && navigator.mediaDevices) {
      navigator.mediaDevices.addEventListener('devicechange', () => {
        void refreshAudioDevices()
      })
    }
    // Native route-change subscription — no-op on web. Mirrors
    // AVAudioSession route notifications into refreshAudioDevices()
    // so the AudioPanel picker reflects hotplug / AirPlay /
    // Bluetooth changes on iOS / Catalyst.
    ensureNativeDeviceChangeSubscription()
  }
  return sharedSink
}

function ensureBus(ctx: AudioContext): FxBus {
  if (sharedBus) return sharedBus
  // P5b — build the multi-channel output graph *before* the master
  // FX bus, so the master FX terminates on the merger's [0, 1] slot
  // rather than directly on the OutputSink. User buses can then
  // either route through master (default, ch 0) or bypass it and
  // land on higher-numbered channel pairs.
  const masterSlot = ensureBusOutputRouter(ctx, ensureOutputSink(ctx).input)
  // Master plugin bus — owns the plugin-kind effect chain on
  // `container.buses[MASTER_BUS_ID].effects` (FFT Analyzer, future
  // LO-FI, etc.). Patched BETWEEN the FxBank and the merger slot
  // so plugin effects see the fully-mastered signal (post-filter /
  // delay / reverb). Before this wire-up the Inspector wrote
  // effects to master but the engine never instantiated them — the
  // master analyzer rendered silent even though its UI row was
  // visible (bug reported 2026-04-23).
  const masterPluginBus = new Bus(ctx, MASTER_BUS_ID)
  masterPluginBus.connectTo(masterSlot)
  sharedBus = createFxBus(ctx, masterPluginBus.input)
  // User mixer buses connect into the master FX bus's input so
  // master effects (filter/delay/reverb/etc.) continue to apply to
  // everything even when a pad is routed through a user bus.
  ensureBusRegistry(ctx, sharedBus.input)
  // Register the plugin bus so `syncBuses` can apply store-side
  // effect changes; also makes `getUserBus(MASTER_BUS_ID)` resolve
  // for the Plugin-tab spectrum view's analyzer-id lookup.
  registerMasterPluginBus(masterPluginBus)
  // Live audio-input pipeline. Stays idle until the user flips the
  // monitor toggle (or Phase 3's record-arm); initialising here
  // just subscribes it to store changes so it's ready to react.
  ensureAudioInput(ctx)
  // P5c multi-channel input router. Same gate (inputMonitorEnabled)
  // so one toggle controls every live input in the app.
  ensureMultiInput(ctx)
  return sharedBus
}

// ── Offline renderer (Plan 2b WAV export) ─────────────────────
//
// Renders a full sequencer project into an AudioBuffer via
// OfflineAudioContext. Used by `lib/exportProject.ts` to turn a
// project into a downloadable WAV. Deliberately skips the master
// FX bank for MVP: the bank is meant to be performed live with
// momentary interaction, so baking a specific "FX off" snapshot
// into the WAV is the least-surprising default. Per-step `fx`
// sends are still applied via a simplified dry-path automation.
//
// Pitch + velocity + sample overrides all apply exactly like
// online playback. Custom samples must be pre-decoded into
// AudioBuffers and passed in — we can't fetch over the network
// inside the render loop because OfflineAudioContext rendering
// is synchronous once started.

export interface OfflineRenderProject {
  bpm: number
  kitId: KitId
  stepCount: number
  steps: Record<
    number,
    Array<{
      on: boolean
      velocity?: number
      pitch?: number
      reverse?: boolean
      /** Gate length in 16th-note units. 1 = one step's worth.
       *  Used by the sample branch to schedule an explicit
       *  `src.stop()` at the end of the step so samples don't
       *  stack hit-after-hit. Optional; defaults to 1 when
       *  omitted (legacy StepCell without length). */
      length?: number
      /** Step-level slice region locks — override the pad's
       *  bound slice for this single step. Live path reads the
       *  same fields; offline didn't until this plumbing. */
      sliceStart?: number
      sliceLen?: number
      /** Step-level envelope lock — merges on top of the pad's
       *  default envelope. Offline reads it alongside the pad
       *  envelope from `samplesByIndex`. */
      envelope?: Partial<Envelope>
      roll?: {
        subdivisions: number
        pitchStart?: number
        pitchEnd?: number
        velocityStart?: number
        velocityEnd?: number
      }
    }>
  >
  /** Pad index → pre-decoded sample AudioBuffer, optional. */
  sampleBuffers?: Map<number, AudioBuffer>
  /**
   * Per-pad voice override. When set for a pad index:
   *   - `VoiceType` — render that procedural voice (overrides
   *     the kit default at that index; also populates slots that
   *     the kit doesn't fill, e.g. 9–15 for the eight-voice kits).
   *   - `null` — skip the procedural path for that pad (used
   *     for pure-sample pads and external-MIDI pads; the latter
   *     produces no audio).
   * Pad indices not present in this map fall back to the kit's
   * voice at that index (legacy behaviour).
   */
  voicesByIndex?: Record<number, VoiceType | null>
  /**
   * Scrambler params — when supplied, applied cell-by-cell in
   * the offline loop so the export matches what the user hears
   * during live playback. Without this the export skipped Scrambler
   * transforms entirely, which produced the "export doesn't match
   * playback" bug. Optional; undefined = no scrambling.
   */
  scramblerParams?: ScramblerParams
  /**
   * Project bus definitions — each bus's FX chain is instantiated
   * on the offline context via the same `Bus` class used in live
   * playback, so per-bus filter / delay / reverb / bitcrush /
   * stutter (plus any registry-backed plugins) all render in the
   * export. Undefined → voices route directly to the destination
   * (legacy behaviour, safe for projects without custom buses).
   */
  buses?: Array<{ id: string; effects?: BusEffectDef[] }>
  /**
   * Pad-index → bus id map for the active bank. Voices fire into
   * the matching bus's input; unmapped pads fall through to the
   * destination. Mirrors the live engine's `resolveBusInput`.
   */
  padBus?: Record<number, string>
  /**
   * Pad-index → registered-instrument descriptor. When set, the
   * offline renderer walks the plugin registry to dispatch that
   * pad's voice (Deep Sub / Pluck Pad / Drone / Sampler / any
   * future registered synth) instead of the procedural or sample
   * branch. Matches what `useSequencerTriggers.ts` does at
   * `kind === 'instrument'` during live playback, so the export
   * includes exactly what users hear.
   *
   * `sampleBuffer` is provided for buffer-backed instruments (the
   * Sampler needs the decoded AudioBuffer injected into its
   * preset; synths ignore it).
   */
  instrumentsByIndex?: Record<
    number,
    {
      instrumentId: string
      preset?: Record<string, number | string>
      sampleBuffer?: AudioBuffer
      sliceStart?: number
      sliceLen?: number
      gainDb?: number
    }
  >
  /**
   * Pad-index → granular dispatch data. When set, the offline
   * renderer dispatches that pad through `Tone.GrainPlayer`
   * inside the Tone.Offline callback so grain character (size,
   * overlap, detune, playback rate, reverse, position, loop)
   * matches live playback. Before this field existed, granular
   * pads silently downgraded to native-rate BufferSource playback.
   *
   * `sampleBuffer` is provided separately by the caller via
   * `sampleBuffers` — granular pads reuse that decode step.
   *
   * Spread params (`positionSpread`, `pitchSpread`) are accepted
   * but NOT rendered offline in this first pass: their live
   * implementation uses `setInterval` to nudge the player mid-
   * playback, which doesn't run on an OfflineAudioContext.
   * Deterministic offline renders snapshot the spread-free
   * baseline; users who need spread in an export should record
   * the live output. Callers may supply spread values anyway —
   * the renderer stores them on the GrainPlayer at start time
   * so the single-shot snapshot still reflects the dialed-in
   * detune + position at t=0.
   */
  granularByIndex?: Record<
    number,
    {
      grainSizeMs: number
      overlap: number
      detuneCents: number
      playbackRate: number
      reverse: boolean
      positionSec: number
      positionSpread: number
      pitchSpread: number
      loop: boolean
      gainDb?: number
    }
  >
  /**
   * Pad-index → sample-pad metadata. Drives the offline sample
   * dispatch to match live playback: pad default ADSR envelope,
   * per-pad gain, and the resolved slice region (sliceStart /
   * sliceLen in buffer seconds) when the pad is bound to a named
   * slice on the underlying sample.
   *
   * Before this field existed, the offline sample branch played
   * every hit as an ungated, unsliced, un-enveloped BufferSource
   * from t to the buffer's natural end — which produced exports
   * where every hit stacked on top of each other because the
   * source never stopped at the step boundary.
   *
   * Optional; undefined → legacy behaviour (naked BufferSource,
   * plays full buffer). That's the pre-fix behaviour and it's
   * still wrong; this field is how callers opt into the gated
   * path. `lib/exportProject.ts` populates it for every
   * `kind: 'sample'` pad.
   */
  samplesByIndex?: Record<
    number,
    {
      envelope?: Partial<Envelope>
      gainDb?: number
      sliceStart?: number
      sliceLen?: number
      /**
       * `'one-shot'` → ignore cell.length for audio gating, let the
       * slice/sample play to its natural end (matches live when
       * cell.length is undefined). `'gated'` → cell.length becomes a
       * hard release tail. Missing → `'one-shot'`.
       *
       * This is the offline mirror of `PadContent.playback` from the
       * shared sample-pad type; `exportProject.ts` forwards the pad
       * field verbatim.
       */
      playback?: 'one-shot' | 'gated'
    }
  >
  /**
   * Master FX bank snapshot — the state of the "perform" surface
   * at export time (filter / delay / reverb / bitcrush / stutter).
   * Each entry is `{ active, level }` mirroring the live
   * `fxState` + `fxLevels` store slices. When present, the
   * renderer instantiates a master FX bus downstream of per-bus
   * chains + direct pad routing and applies the snapshot state
   * as a constant engagement for the full render duration. No
   * automation — the master FX is a live-only perform surface
   * today, so snapshot is the best-effort representation.
   *
   * `tapeStop` is deliberately skipped because it's a one-shot
   * engage ramp with no meaningful "level" semantics for a
   * duration-long snapshot; users who want a tape-stop moment
   * in the export should record the live output.
   */
  masterFx?: Partial<
    Record<
      'filter' | 'delay' | 'reverb' | 'bitcrush' | 'stutter',
      { active: boolean; level: number }
    >
  >
}

/** Subset of BusEffect the offline renderer consumes. Mirrors the
 *  shared type but isn't imported as-is to avoid a shared-package
 *  dependency at this layer. */
interface BusEffectDef {
  id: string
  kind: 'filter' | 'delay' | 'reverb' | 'bitcrush' | 'stutter' | 'plugin'
  pluginId?: string
  enabled?: boolean
  wet?: number
  params?: Record<string, number>
}

/**
 * Module-level defaults for the gated-sample helper — kept here
 * (not in `scheduleSample`'s closure) so the offline render path
 * can reference them without instantiating the hook. Duplicated
 * intentionally: the hook's `DEFAULT_ENVELOPE` has the same
 * values but different scope. Keep both in sync if you change
 * one — there's a test that would catch drift if we added one.
 */
const GATED_SAMPLE_DEFAULT_ENV = {
  a: 0.001,
  d: 0,
  s: 1,
  r: 0.02,
} as const

/**
 * Pure (context-agnostic) scheduler for a single sample hit with
 * ADSR + gate. Called by both the live sample fire path
 * (`scheduleSample` inside `useAudioEngine`) and the offline
 * renderer's sample branch in `renderIntoOfflineContext`.
 *
 * What this function owns:
 *   - Build a `BufferSourceNode` + `GainNode` on the supplied ctx
 *   - Apply `playbackRate` from `pitchSemitones` (unless
 *     `timeStretch` is supplied, in which case rate = stretch ×
 *     pitchRatio, matching the live path's behaviour)
 *   - Compute the "real-world" region length (slice-aware, rate-
 *     aware) and use it as the default gate when the caller
 *     didn't specify one
 *   - Write a full ADSR envelope on the gain node
 *   - Schedule `src.stop()` at the release tail so the source
 *     releases the node slot even if the buffer is much longer
 *     than the step — this is the fix for the "sample plays in
 *     full, overlapping" export bug
 *   - Start the source, honouring `sliceStart` + `sliceLen` when
 *     both are provided
 *
 * What it deliberately does NOT own:
 *   - Rubber Band / BPM-conform worklet routing. That stays in
 *     the live `scheduleSample` because the pool is live-only;
 *     offline renders don't support it (yet).
 *   - Reversed-buffer resolution. Callers pass a pre-reversed
 *     buffer when they want reverse playback — live uses its
 *     own cache keyed by sampleId, offline uses a per-render map.
 *   - MIDI dispatch. This is audio-only.
 */
export function scheduleGatedSample(
  ctx: AudioContext | OfflineAudioContext,
  dest: AudioNode,
  buffer: AudioBuffer,
  opts: {
    when: number
    velocity: number
    pitchSemitones?: number
    sliceStart?: number
    sliceLenSec?: number
    timeStretch?: number
    envelope?: Partial<Envelope>
    /** Gate length in context seconds. When omitted the gate
     *  defaults to the sample's natural region-length (possibly
     *  shortened by `sliceLenSec`), which keeps short one-shot
     *  samples sounding natural while long loops get cut at step
     *  boundaries via a caller-supplied gate. */
    gateSec?: number
  },
): void {
  const {
    when,
    velocity,
    pitchSemitones = 0,
    sliceStart,
    sliceLenSec,
    timeStretch,
    envelope,
    gateSec,
  } = opts
  const src = (ctx as AudioContext).createBufferSource()
  src.buffer = buffer
  const stretch = timeStretch ?? 1
  const pitchRatio = pitchSemitones !== 0 ? 2 ** (pitchSemitones / 12) : 1
  const rate = stretch * pitchRatio
  if (rate !== 1) src.playbackRate.value = rate

  const gain = ctx.createGain()

  // Region length in real-world seconds after playbackRate scaling.
  // Used both to default the gate to natural length AND to decide
  // when to call `src.stop()` so we don't keep ungated sources
  // running forever in offline renders.
  const safeSliceStart =
    sliceStart !== undefined
      ? Math.max(0, Math.min(buffer.duration, sliceStart))
      : 0
  const bufferTail = buffer.duration - safeSliceStart
  const regionLenSrc =
    sliceLenSec !== undefined && sliceLenSec > 0
      ? Math.min(sliceLenSec, bufferTail)
      : bufferTail
  const regionLenReal = regionLenSrc / Math.max(0.01, rate)

  const env: Envelope = {
    a: envelope?.a ?? GATED_SAMPLE_DEFAULT_ENV.a,
    d: envelope?.d ?? GATED_SAMPLE_DEFAULT_ENV.d,
    s: envelope?.s ?? GATED_SAMPLE_DEFAULT_ENV.s,
    r: envelope?.r ?? GATED_SAMPLE_DEFAULT_ENV.r,
  }
  // Gate defaults to the region's natural play-through length so
  // a hit without a step-level note-off still gets a release tail
  // and stops at the end of the slice / buffer — NOT after the
  // buffer is done, stacked on top of every subsequent hit.
  const gate = Math.max(0, gateSec ?? regionLenReal)
  const peakT = when + Math.max(0, env.a)
  const sustainT = peakT + Math.max(0, env.d)
  // Ensure monotonically increasing times — AudioParam rejects
  // schedules that go backwards.
  const releaseT = Math.max(sustainT, when + gate)
  const endT = releaseT + Math.max(0, env.r)
  gain.gain.setValueAtTime(0, when)
  gain.gain.linearRampToValueAtTime(velocity, peakT)
  gain.gain.linearRampToValueAtTime(velocity * env.s, sustainT)
  gain.gain.setValueAtTime(velocity * env.s, releaseT)
  gain.gain.linearRampToValueAtTime(0, endT)

  src.connect(gain).connect(dest)

  if (
    sliceStart !== undefined &&
    sliceLenSec !== undefined &&
    sliceLenSec > 0
  ) {
    const safeLen = Math.min(sliceLenSec, buffer.duration - safeSliceStart)
    if (safeLen > 0) src.start(when, safeSliceStart, safeLen)
    else src.start(when)
  } else if (safeSliceStart > 0) {
    src.start(when, safeSliceStart)
  } else {
    src.start(when)
  }
  // Hard stop at the envelope's end so the source doesn't keep
  // running past the gate. BufferSourceNode.stop is idempotent
  // when the source has already ended naturally, so the `try`
  // only swallows the Safari "start-in-past" quirk on stops that
  // are already due.
  try {
    src.stop(endT + 0.01)
  } catch {
    /* stop already scheduled or source ended */
  }
}

/** Offline counterpart to the engine's `getReversedBuffer` —
 *  keyed by buffer reference so we don't need a sampleId map.
 *  Creates reversed buffers on an OfflineAudioContext which is
 *  what the render loop uses. */
function reverseOfflineBuffer(
  ctx: OfflineAudioContext,
  forward: AudioBuffer,
  cache: Map<AudioBuffer, AudioBuffer>,
): AudioBuffer {
  const cached = cache.get(forward)
  if (cached) return cached
  const channels = forward.numberOfChannels
  const reversed = ctx.createBuffer(
    channels,
    forward.length,
    forward.sampleRate,
  )
  for (let ch = 0; ch < channels; ch++) {
    const src = forward.getChannelData(ch)
    const dst = reversed.getChannelData(ch)
    dst.set(Float32Array.from(src).reverse())
  }
  cache.set(forward, reversed)
  return reversed
}

export async function renderProjectOffline(
  project: OfflineRenderProject,
): Promise<AudioBuffer> {
  const sampleRate = 44100
  // 16th note duration in seconds at the project's BPM.
  const stepSec = 60 / project.bpm / 4
  // Total project length + 0.5s tail to let voice decays ring out.
  const durationSec = project.stepCount * stepSec + 0.5

  // Wrap the whole render in `Tone.Offline` so Tone-backed nodes
  // (GrainPlayer, the 11 registered Tone effects) use the offline
  // context natively instead of leaking onto the live one. Before
  // this wrap, granular pads silently downgraded to native-rate
  // BufferSource playback (see the previous comment in
  // exportProject.ts) and Tone FX on buses got the offline
  // context via a cast that didn't actually call `setContext`, so
  // their internal routing was wrong. With Tone.Offline, both
  // subsystems "just work" — Tone.getContext() is the offline
  // context for the duration of the callback, and raw Web Audio
  // calls route through `toneGetContext().rawContext` which is
  // the real OfflineAudioContext.
  const toneBuffer = await ToneOffline(async () => {
    const ctx = toneGetContext().rawContext as OfflineAudioContext
    // Belt-and-suspenders: `Tone.Offline` already calls
    // `setContext` internally for the duration of the callback, but
    // syncing our own `registerToneContext` tracking state keeps
    // the shared helper's internal `registeredCtx` accurate so
    // any debug accessors or future callers that look up the
    // current Tone context see the offline one. Idempotent — the
    // helper short-circuits when the current Tone context already
    // matches. This also documents the expectation for Tone-
    // registered bus FX (Chorus / Compressor / EQ3 / AutoFilter /
    // Phaser / Tremolo / Vibrato / PitchShift / Limiter /
    // SweetWhistle): they instantiate inside this callback and see
    // the offline context via Tone's internal lookup.
    registerToneContext(ctx)
    await renderIntoOfflineContext(project, ctx, stepSec)
  }, durationSec)

  // Restore the live context in our tracking state. Tone.Offline
  // auto-restores its own internal context when the callback
  // exits, but our `registeredCtx` cache still points at the
  // (now-gone) offline context. Rewriting it back to whatever
  // Tone now considers current prevents a stale-context bug on
  // the next live trigger that hits the tracker.
  registerToneContext(toneGetContext().rawContext)

  // `Tone.Offline` returns a ToneAudioBuffer. Unwrap to the raw
  // AudioBuffer the rest of the pipeline (WAV encoder etc.) expects.
  return toneBuffer.get() as AudioBuffer
}

/** The body of the offline render, separated so Tone.Offline can
 *  invoke it with its managed context. Everything here runs
 *  inside the Tone.Offline callback where `toneGetContext()` is
 *  the OfflineAudioContext. */
async function renderIntoOfflineContext(
  project: OfflineRenderProject,
  ctx: OfflineAudioContext,
  stepSec: number,
): Promise<void> {
  // Reversed-buffer cache — keyed by the forward buffer reference so
  // the reverse only happens once per sample even when reverse is
  // triggered on multiple steps (or via Scrambler's reverseEveryN).
  const reversedOfflineCache = new Map<AudioBuffer, AudioBuffer>()

  // Per-bus FX rendering — construct a Bus instance per project bus
  // on the offline context, populate its FX chain, wire each to the
  // destination. Voice routing later looks up its bus via `padBus`
  // and feeds `bus.input` instead of `ctx.destination`. Without this
  // the export would skip every per-bus filter / delay / reverb /
  // plugin the user added — one of the divergences called out in
  // PLAN §F4. Master bus + pads with no explicit bus still go
  // direct-to-destination via `defaultDest` below.
  // Master FX bus — when the caller passed a `masterFx` snapshot,
  // instantiate the perform-bank chain on the offline context and
  // engage each effect at its snapshot state. The bus sits between
  // per-bus chains + direct pad routing and `ctx.destination` so
  // both paths pick up the same filter / delay / reverb / bitcrush
  // / stutter tail. `tapeStop` is deliberately not snapshot-able.
  // When no snapshot is provided, the bus isn't built and routing
  // stays direct-to-destination (legacy behaviour).
  const fxBus = project.masterFx
    ? createFxBus(ctx as unknown as AudioContext, ctx.destination)
    : null
  if (fxBus && project.masterFx) {
    for (const [id, entry] of Object.entries(project.masterFx)) {
      if (!entry) continue
      fxBus.engage(id as FxId, entry.active, entry.level)
    }
  }
  const busDestination: AudioNode = fxBus ? fxBus.input : ctx.destination

  const busById = new Map<string, Bus>()
  if (project.buses) {
    for (const def of project.buses) {
      const b = new Bus(ctx as unknown as AudioContext, def.id)
      if (def.effects && def.effects.length > 0) {
        b.setEffects(def.effects as unknown as BusEffect[])
      }
      b.connectTo(busDestination)
      busById.set(def.id, b)
    }
  }
  const defaultDest: AudioNode = busDestination
  const resolveDest = (padIndex: number): AudioNode => {
    const busId = project.padBus?.[padIndex]
    if (!busId) return defaultDest
    const bus = busById.get(busId)
    return bus?.input ?? defaultDest
  }
  const dest = defaultDest

  // Map pad index to kit voice type via the same lookup the
  // online path uses. With `voicesByIndex` overrides we can walk
  // the full 16-slot grid rather than just the kit's 8.
  const kitVoices = KITS[project.kitId]?.voices ?? KITS.classic.voices
  const voicesByIndex = project.voicesByIndex ?? {}
  const PAD_COUNT = 16

  for (let padIndex = 0; padIndex < PAD_COUNT; padIndex++) {
    const row = project.steps[padIndex]
    if (!row) continue

    const sampleBuffer = project.sampleBuffers?.get(padIndex)
    const instrumentSpec = project.instrumentsByIndex?.[padIndex]
    // Resolve the voice for this pad: override map wins, else
    // the kit's default at that index, else null (no synth).
    let voiceType: VoiceType | null
    if (padIndex in voicesByIndex) {
      voiceType = voicesByIndex[padIndex]
    } else if (instrumentSpec) {
      // Instrument-kind pads handle their own voicing via the
      // plugin registry; the procedural branch is disabled for
      // them so we don't double-fire.
      voiceType = null
    } else {
      voiceType = kitVoices[padIndex]?.type ?? null
    }

    // Skip entirely when there's nothing to render (MIDI-only
    // pads or empty slots with no sample, no voice, no
    // instrument).
    if (!sampleBuffer && voiceType === null && !instrumentSpec) continue

    for (let stepIdx = 0; stepIdx < row.length; stepIdx++) {
      const rawCell = row[stepIdx]
      if (!rawCell || !rawCell.on) continue
      // Apply Scrambler transforms (velocity / pitch jitter,
      // probability drop, reverse-every-N, downbeat stutter-roll)
      // before firing so the export matches live playback.
      // `applyScramblerParams` returns `null` when the step should
      // be dropped entirely. When scramblerParams is absent or
      // disabled, `applyScramblerParams` returns the cell unchanged.
      // Cast through unknown — the offline StepCell shape is a
      // structural subset of the full library StepCell, matching the
      // fields applyScramblerParams actually reads.
      const cell = project.scramblerParams
        ? applyScramblerParams(
            rawCell as unknown as StepCell,
            project.scramblerParams,
            padIndex,
            stepIdx,
            row.length,
            row as unknown as ReadonlyArray<{ on: boolean }>,
            project.padBus?.[padIndex],
          )
        : rawCell
      if (!cell) continue
      const when = stepIdx * stepSec
      const baseVel = Math.max(0.05, Math.min(1, cell.velocity ?? 0.9))
      const pitch = cell.pitch ?? 0
      // Roll expansion — a cell with `roll.subdivisions` fires N
      // equally-spaced sub-hits within the step window, optionally
      // tracking pitch + velocity from start to end. Matches the
      // live-trigger path's behaviour on the sample branch; on
      // the procedural voice branch we still emit one hit per
      // sub-division since `triggerVoice` is allocation-heavy.
      const roll = cell.roll
      const subCount =
        roll && roll.subdivisions > 1 ? Math.floor(roll.subdivisions) : 1
      const subStep = stepSec / subCount
      for (let i = 0; i < subCount; i++) {
        const t = when + i * subStep
        const tNorm = subCount > 1 ? i / (subCount - 1) : 0
        const subVelStart = roll?.velocityStart ?? baseVel
        const subVelEnd = roll?.velocityEnd ?? baseVel
        const subVel = Math.max(
          0.05,
          Math.min(1, subVelStart + (subVelEnd - subVelStart) * tNorm),
        )
        const subPitchStart = roll?.pitchStart ?? pitch
        const subPitchEnd = roll?.pitchEnd ?? pitch
        const subPitch = subPitchStart + (subPitchEnd - subPitchStart) * tNorm

        // Route to the pad's configured bus if buses are present;
        // otherwise direct-to-destination (legacy behaviour). Done
        // per-hit inside the inner loop so future per-step bus
        // overrides slot in without restructuring.
        const voiceDest = resolveDest(padIndex)

        // Granular dispatch — when this pad has a granular spec
        // (from `project.granularByIndex`), build a `Tone.GrainPlayer`
        // inside the Tone.Offline context and let it handle grain
        // emission. Mirrors the live `scheduleGranular` path but
        // without the setInterval-based spread emulation, which
        // can't run on an OfflineAudioContext — spread at t=0 is
        // snapshotted below, post-start jitter is not applied.
        // Falls through to the sampleBuffer / voiceType branches
        // when no granular spec is supplied, preserving the
        // pre-A2 behaviour on projects that had no granular pads.
        const granularSpec = project.granularByIndex?.[padIndex]
        if (granularSpec && sampleBuffer) {
          const toneBuffer = new ToneAudioBuffer()
          toneBuffer.set(sampleBuffer)
          const player = new GrainPlayer(toneBuffer)

          const grainSec = Math.max(
            0.005,
            Math.min(2, granularSpec.grainSizeMs / 1000),
          )
          player.grainSize = grainSec
          player.overlap = Math.max(0, Math.min(1, granularSpec.overlap))
          const baseDetune = Math.max(
            -2400,
            Math.min(2400, granularSpec.detuneCents + subPitch * 100),
          )
          player.detune = baseDetune
          player.playbackRate = Math.max(
            0.25,
            Math.min(4, granularSpec.playbackRate),
          )
          player.reverse = granularSpec.reverse
          player.loop = granularSpec.loop
          if (granularSpec.loop) {
            const loopWin = Math.min(0.5, sampleBuffer.duration)
            const ls = Math.max(0, granularSpec.positionSec)
            player.loopStart = Math.min(sampleBuffer.duration - 0.01, ls)
            player.loopEnd = Math.min(sampleBuffer.duration, ls + loopWin)
          }

          // Gate-shaped gain overlay so granular pads respect
          // per-step velocity. Matches the live path's ADSR shape
          // in miniature (attack + short release). Offline has no
          // opts.envelope hook yet, so we default to DEFAULT_ENVELOPE.
          const gain = ctx.createGain()
          const gate = stepSec * 0.95
          const peakT = t + DEFAULT_ENVELOPE.a
          const releaseT = t + gate
          const endT = releaseT + DEFAULT_ENVELOPE.r
          const padGain = dbToLinear(granularSpec.gainDb ?? 0)
          const vel = subVel * padGain
          gain.gain.setValueAtTime(0, t)
          gain.gain.linearRampToValueAtTime(vel, peakT)
          gain.gain.setValueAtTime(vel, releaseT)
          gain.gain.linearRampToValueAtTime(0, endT)

          player.connect(gain)
          gain.connect(voiceDest)

          const startOffset = Math.max(
            0,
            Math.min(sampleBuffer.duration - 0.01, granularSpec.positionSec),
          )
          const playDur = endT - t
          try {
            player.start(t, startOffset, playDur)
          } catch {
            /* Start-in-past on some Tone builds — re-attempt
               without explicit time so the player at least emits
               at the earliest available offline tick. */
            try {
              player.start(undefined, startOffset, playDur)
            } catch {
              player.dispose()
              toneBuffer.dispose()
            }
          }
          continue
        }

        if (instrumentSpec) {
          // Registered-instrument dispatch on the offline context.
          // Mirrors `triggerInstrument` on the live engine: look the
          // plugin up by id, build a voice with the merged preset
          // (buffer + slice fields for the Sampler), trigger, and
          // schedule a release + natural-tail cleanup. The
          // OfflineAudioContext decays everything at the end of
          // `startRendering`, so disposal is largely cosmetic —
          // kept for symmetry with the live path and to free nodes
          // for plugins that hold heavy state.
          const plugin = getInstrument(instrumentSpec.instrumentId)
          if (!plugin) continue
          const preset: Record<string, InstrumentPresetValue> = {
            ...(instrumentSpec.preset ?? {}),
          }
          if (instrumentSpec.sampleBuffer) {
            preset.buffer = instrumentSpec.sampleBuffer
          }
          if (typeof instrumentSpec.sliceStart === 'number') {
            preset.sliceStart = instrumentSpec.sliceStart
          }
          if (typeof instrumentSpec.sliceLen === 'number') {
            preset.sliceLen = instrumentSpec.sliceLen
          }
          const voice = plugin.createVoice(
            ctx as unknown as AudioContext,
            preset,
          )
          try {
            voice.output.connect(voiceDest)
          } catch {
            /* already connected */
          }
          const padGain = dbToLinear(instrumentSpec.gainDb ?? 0)
          voice.trigger(t, subVel * padGain, subPitch)
          // Gate release ≈ the step duration so sustained
          // instruments (Drone, Pluck Pad) don't bleed into
          // subsequent steps. Longer tails ride on the voice's
          // internal release envelope.
          if (voice.release) voice.release(t + stepSec * 0.95)
          continue
        }

        if (sampleBuffer) {
          // Routed through the shared `scheduleGatedSample` helper so
          // every sample hit gets an ADSR ramp + an explicit
          // `src.stop()` at the envelope tail. Before this, the
          // offline sample branch played the whole buffer on every
          // hit and relied on nothing to stop it — 16 hits over a
          // 2-second sample produced 16 overlapping playbacks that
          // smeared the mix. `samplesByIndex` carries the pad's
          // default envelope + resolved slice region; cell-level
          // step locks layer on top via `cellEnvelope` + `gateSec`.
          const sampleMeta = project.samplesByIndex?.[padIndex]
          const playBuffer = cell.reverse
            ? reverseOfflineBuffer(ctx, sampleBuffer, reversedOfflineCache)
            : sampleBuffer
          // Step-level gate semantics — matches the live fire path
          // (`useSequencerTriggers.ts` sample branch):
          //
          //   gateSec = cell.length !== undefined
          //             ? cell.length * stepSec
          //             : undefined
          //
          // Then the pad-level `playback` mode gets the final say:
          // `'one-shot'` (default) discards gateSec entirely so each
          // hit plays to its natural slice/sample end — matching the
          // 99% chopped-sample case where every slice should ring out
          // regardless of cell-length locks.
          //
          // Before this fix, the offline branch defaulted cellLen to
          // 1 and always passed the gate, so chopped samples with
          // undefined cell.length (the default) got cut off at the
          // 16th-note boundary in exports even though live plays them
          // through. That was the over-gating regression reported
          // after PR #209.
          const explicitGate =
            typeof cell.length === 'number' ? cell.length * stepSec : undefined
          const playbackMode = sampleMeta?.playback ?? 'one-shot'
          const cellGate = playbackMode === 'gated' ? explicitGate : undefined
          // Slice region — pad-level slice binding from samplesByIndex
          // (resolved at exportProject time against the sample's
          // slice list), overridden per-step by cell.sliceStart /
          // cell.sliceLen if the user stamped a slice lock.
          let sliceStart =
            typeof cell.sliceStart === 'number'
              ? cell.sliceStart
              : sampleMeta?.sliceStart
          const sliceLen =
            typeof cell.sliceLen === 'number'
              ? cell.sliceLen
              : sampleMeta?.sliceLen
          // Reverse + slice coordinate flip: live path does this in
          // `fireOneHit`; replicate it here so reversed sliced pads
          // play the expected region.
          if (
            cell.reverse &&
            sliceStart !== undefined &&
            sliceLen !== undefined
          ) {
            sliceStart = playBuffer.duration - (sliceStart + sliceLen)
          }
          const cellEnvelope =
            cell.envelope || sampleMeta?.envelope
              ? { ...sampleMeta?.envelope, ...cell.envelope }
              : undefined
          const padGainMult = dbToLinear(sampleMeta?.gainDb ?? 0)
          scheduleGatedSample(ctx, voiceDest, playBuffer, {
            when: t,
            velocity: subVel * padGainMult,
            pitchSemitones: subPitch,
            sliceStart,
            sliceLenSec: sliceLen,
            envelope: cellEnvelope,
            gateSec: cellGate,
          })
          continue
        }

        if (voiceType === null) continue

        triggerVoice(
          ctx as unknown as AudioContext,
          voiceDest,
          project.kitId,
          voiceType,
          t,
          subVel,
          subPitch,
        )
      }
    }
  }
}

// ── Public hook ─────────────────────────────────────────────

/**
 * ADSR parameters used by `scheduleSample`. Kept local to the
 * engine so the store's `Envelope` type (which may grow
 * sequencer-specific fields later) stays decoupled from the
 * audio path. Conversion happens at the `trigger()` boundary —
 * see the sample branch below.
 */
interface SampleEnvelope {
  a: number
  d: number
  s: number
  r: number
}
const DEFAULT_ENVELOPE: SampleEnvelope = { a: 0.001, d: 0, s: 1, r: 0.02 }

/**
 * Returns the audio engine's stable API. Callers are responsible for
 * resuming the context in a user gesture (typically via `resume()`
 * from a play/pad click handler), otherwise Safari/Chrome will
 * return a suspended context and produce no sound.
 */
export function useAudioEngine() {
  const ctxRef = useRef<AudioContext | null>(null)

  useEffect(() => {
    const ctx = ensureContext()
    ctxRef.current = ctx
    // Share our AudioContext with Tone so granular pads (and the
    // slicer's GrainPlayer preview) route through the same graph
    // as the rest of the engine. Without this, Tone creates its own
    // AudioContext and GrainPlayer audio bypasses our FX bus. The
    // helper dedupes repeated calls so hot-reload / re-mount stays
    // quiet.
    if (ctx) registerToneContext(ctx)
    // Seed the plugin registry with the built-in effects + instruments +
    // the Tone-backed effects (chorus, compressor, limiter, EQ3).
    // Idempotent, so hot-reload / re-mount doesn't double-register. The
    // existing hardcoded `Bus.buildSlot` + `triggerVoice` switches stay
    // authoritative; this just makes the kinds discoverable via the
    // generic picker UI that'll land alongside the full migration.
    registerBuiltinPlugins()
    registerBuiltinInstruments()
    registerToneEffects()
    registerFftAnalyzer()
    registerLoFiEffect()
    registerLfoTremoloEffect()
    // Creative Tone-backed instruments (PLAN §F6). Each factory
    // registers once and is safely no-op on re-mount.
    registerDeepSubInstrument()
    registerPluckPadInstrument()
    registerDroneInstrument()
    // Bulk-register any first-party WAMs declared in WAM_BUNDLE.
    // Empty bundle is a no-op; Phase C fills it with MIT/BSD-licensed
    // plugins auditioned against real audio. Async by design — the
    // preload cache pays the import cost once so Bus.buildSlot stays
    // synchronous at the pad-trigger hot path.
    if (ctx) void registerWams(ctx, WAM_BUNDLE)
  }, [])

  const resume = useCallback(async () => {
    const ctx = ensureContext()
    ctxRef.current = ctx
    if (ctx && ctx.state === 'suspended') await ctx.resume()
    return ctx
  }, [])

  /**
   * Optional per-step dispatches passed to `trigger`. Defaults kick
   * in when undefined: default velocity 0.9, no pitch shift, no
   * per-step FX sends.
   */
  interface TriggerOptions {
    velocity?: number
    /** Semitones offset; -24..+24. 0 = no change. */
    pitchSemitones?: number
    /** FX sends as 0..1 per key. Scheduler automates wet gains. */
    fx?: Partial<Record<FxId, number>>
    /**
     * Per-step ADSR envelope for procedural / synth voices. When
     * set, the voice's output is wrapped in a gain node whose
     * automation ramps `0 → 1 → sustain → 0` across A+D+gate+R.
     * Multiplicative on top of whatever internal shape the voice
     * generates, so kit character stays intact; the envelope
     * extends or shortens the audible tail.
     *
     * Sample pads keep reading envelope from `opts.sample.envelope`
     * (which also controls release-tail timing inside the sample
     * fire path). Top-level `envelope` is for the synth branch.
     */
    envelope?: Partial<SampleEnvelope>
    /**
     * Gate length in seconds for the envelope's sustain portion.
     * Must be set alongside `envelope` for the release to align
     * with the step length. Derived in the scheduler from cell
     * length × stepSec.
     */
    gateSec?: number
    /**
     * Plan 2b: when set, replace the procedural voice with an
     * uploaded sample. The engine looks up the AudioBuffer in its
     * cache (keyed by `sampleId`); miss = fall through to the
     * procedural voice for this hit and trigger an async fetch so
     * the next hit gets the sample. Pitch + velocity still apply.
     */
    sample?: {
      sampleId: string
      playUrl?: string
      /**
       * Optional slice region within the buffer. When both fields are
       * set the engine plays only `[sliceStart, sliceStart+sliceLenSec)`
       * via `AudioBufferSourceNode.start(when, offset, duration)` —
       * no manual buffer-copying, no gap-scheduling. Omit for full-
       * sample playback (pre-slicer behaviour).
       */
      sliceStart?: number
      sliceLenSec?: number
      /**
       * Phase 3 per-step manipulation. `reverse` swaps in a lazily-
       * cached reversed AudioBuffer. `timeStretch` multiplies into
       * playbackRate (stacks with pitch). `envelope` applies an
       * ADSR gain ramp (missing fields use engine defaults).
       * `gateSec` is how long the sustain holds before release —
       * used by the sequencer to respect per-step gate length;
       * omit for "play the whole region."
       */
      reverse?: boolean
      timeStretch?: number
      envelope?: Partial<SampleEnvelope>
      gateSec?: number
      /**
       * Phase 5 — realtime BPM conform. When both `sourceBpm` and
       * `conformBpm` are set and differ, the hit routes through a
       * Rubber Band AudioWorklet that stretches tempo without
       * pitch-shifting. Callers (sequencer fire path) pass
       * `sourceBpm = sample.bpm` and `conformBpm = project.bpm`
       * only when the project has `conformToProjectBpm` on and the
       * sample actually has a known source BPM. Missing either =
       * fall through to the naïve playbackRate path.
       */
      sourceBpm?: number
      conformBpm?: number
    }
    /**
     * Phase 4 — granular pad. When set, replaces the procedural voice
     * for this hit with a per-hit Tone.GrainPlayer configured from
     * the pad's stored grain params. Pitch / velocity / gate are
     * applied on top via the shared ADSR gain overlay so the pad
     * obeys step envelope + sequencer dynamics like every other kind.
     */
    granular?: {
      sampleId: string
      playUrl?: string
      grainSizeMs: number
      overlap: number
      detuneCents: number
      playbackRate: number
      reverse: boolean
      positionSec: number
      positionSpread: number
      pitchSpread: number
      loop: boolean
      envelope?: Partial<SampleEnvelope>
      gateSec?: number
    }
    /**
     * Phase 2b: mixer bus routing. When set + the bus exists, the
     * voice connects to that bus's input instead of the master
     * FX-bus input directly. The bus then forwards (with its own
     * volume + pan) into master, so master FX still apply to
     * everything. Unknown / missing id → falls back to master.
     */
    busId?: string
  }

  /**
   * AudioBuffer cache keyed by sampleId. Populated on demand by the
   * first playback attempt — Web Audio `decodeAudioData` is
   * expensive, so a single decode per sample is reused for every
   * subsequent trigger. Also caches inflight fetch promises so a
   * burst of rapid triggers against the same sample only issues one
   * network fetch.
   */
  const sampleBufferCache = useRef(new Map<string, AudioBuffer>())
  const sampleFetchInflight = useRef(
    new Map<string, Promise<AudioBuffer | null>>(),
  )

  // ── Live-voice registry (attack / release split) ───────────────
  //
  // Surfaces with real key-hold semantics (virtual keyboard, MIDI
  // router, Piano Roll live audition) call `attackInstrument()` to
  // start a sustained voice and `releaseInstrument(token)` when the
  // key comes up. Registry is keyed by an opaque incrementing token so
  // concurrent voices on the same pad/pitch are distinguishable
  // (multiple keyboard keys chorded on Deep Sub = N live entries).
  //
  // Safety net: every entry carries a `maxHoldDeadline` timer that
  // force-releases after 30 s. Stops a browser tab-switch or a
  // dropped pointerup event from leaving notes ringing forever.
  const liveVoicesRef = useRef<
    Map<
      number,
      {
        voice: InstrumentVoiceHandle
        ctx: AudioContext
        released: boolean
        forceReleaseTimeout: number | null
      }
    >
  >(new Map())
  const voiceTokenCounterRef = useRef(0)

  const fetchSampleBuffer = useCallback(
    async (
      ctx: AudioContext,
      sampleId: string,
      playUrl: string,
    ): Promise<AudioBuffer | null> => {
      const cached = sampleBufferCache.current.get(sampleId)
      if (cached) return cached
      const inflight = sampleFetchInflight.current.get(sampleId)
      if (inflight) return inflight

      const promise = (async () => {
        try {
          const res = await fetch(playUrl)
          if (!res.ok) return null
          const bytes = await res.arrayBuffer()
          const buffer = await ctx.decodeAudioData(bytes)
          sampleBufferCache.current.set(sampleId, buffer)
          return buffer
        } catch (err) {
          console.warn(`Failed to load sample ${sampleId}:`, err)
          return null
        } finally {
          sampleFetchInflight.current.delete(sampleId)
        }
      })()
      sampleFetchInflight.current.set(sampleId, promise)
      return promise
    },
    [],
  )

  /**
   * Reversed-buffer cache. Keyed by `sampleId`; populated lazily the
   * first time a reverse-flagged hit needs it. Reversing a buffer
   * is O(N) in both time and memory, so we do it once per sample
   * and reuse the result for every subsequent reversed hit. The
   * cache survives as long as the forward buffer cache does — no
   * separate eviction — since the two are always paired.
   */
  const reversedBufferCache = useRef(new Map<string, AudioBuffer>())

  const getReversedBuffer = useCallback(
    (
      ctx: AudioContext,
      sampleId: string,
      forward: AudioBuffer,
    ): AudioBuffer => {
      const cached = reversedBufferCache.current.get(sampleId)
      if (cached) return cached
      const channels = forward.numberOfChannels
      const reversed = ctx.createBuffer(
        channels,
        forward.length,
        forward.sampleRate,
      )
      for (let ch = 0; ch < channels; ch++) {
        const src = forward.getChannelData(ch)
        const dst = reversed.getChannelData(ch)
        // Single pass from both ends meeting in the middle would
        // also work but `.slice().reverse()` lets V8 use its
        // optimised typed-array copy path.
        dst.set(Float32Array.from(src).reverse())
      }
      reversedBufferCache.current.set(sampleId, reversed)
      return reversed
    },
    [],
  )

  /**
   * Schedule a sample playback. `buffer` is an already-decoded
   * AudioBuffer from the cache; pitch shift and time-stretch are
   * combined into `playbackRate`, velocity and any ADSR envelope
   * are applied via a pre-dest GainNode.
   *
   * When `sliceStart`/`sliceLenSec` are supplied, only that region
   * of the buffer is played — Web Audio's built-in `start(when,
   * offset, duration)` handles the gating, so we don't have to
   * copy bytes out of the buffer for every slice. Region bounds
   * are clamped against the buffer's real duration so Safari
   * doesn't throw on a rounding-overshoot at the tail.
   *
   * Reversed playback is handled by swapping in a reversed buffer
   * at the call site; within this function the slice region is
   * interpreted relative to *this* buffer's time axis. The caller
   * must translate `(origStart, origEnd)` into the reversed
   * buffer's space — see the reversed branch in `trigger()`.
   *
   * Envelope behaviour: passing `undefined` preserves the legacy
   * flat-gain path (velocity is set once, no ramps). Passing any
   * envelope triggers full ADSR automation — attack ramps gain 0
   * → velocity, decay falls to `velocity * s`, sustain holds for
   * `gateSec`, release ramps to 0 over `r` seconds. Missing
   * envelope fields fall back to DEFAULT_ENVELOPE.
   */
  const scheduleSample = useCallback(
    (
      ctx: AudioContext,
      dest: AudioNode,
      buffer: AudioBuffer,
      when: number,
      velocity: number,
      pitchSemitones: number,
      sliceStart?: number,
      sliceLenSec?: number,
      timeStretch?: number,
      envelope?: Partial<SampleEnvelope>,
      gateSec?: number,
      /** Phase 5 — when both `sourceBpm` and `conformBpm` are
       *  provided AND differ, the sample routes through a Rubber
       *  Band worklet that stretches tempo without pitch-shifting.
       *  Missing either param falls back to the naïve playbackRate
       *  path above. Pool-based, sync; exhausted pool = fallback. */
      sourceBpm?: number,
      conformBpm?: number,
    ) => {
      const src = ctx.createBufferSource()
      src.buffer = buffer
      const stretch = timeStretch ?? 1
      const pitchRatio = pitchSemitones !== 0 ? 2 ** (pitchSemitones / 12) : 1

      // Try conform path first. Falls back to playbackRate below on
      // any bail-out (missing BPM, ratio = 1, pool empty).
      let rbNode: RubberBandNode | null = null
      const canConform =
        sourceBpm !== undefined &&
        sourceBpm > 0 &&
        conformBpm !== undefined &&
        conformBpm > 0 &&
        Math.abs(sourceBpm - conformBpm) > 0.05
      if (canConform) {
        // Warm the pool on first conform attempt so subsequent hits
        // always find a node. Safe to call repeatedly — internal
        // guard de-dupes.
        warmRbPool(ctx)
        const conformTempoRatio = sourceBpm / conformBpm
        // Pitch + stretch still combine, but semitones are handled
        // by the worklet's pitchSemitones param (pitch-preserved),
        // and extra timeStretch multiplies the tempo ratio.
        rbNode = borrowRbNode(conformTempoRatio * stretch, pitchSemitones)
      }

      const useConform = rbNode !== null
      const rate = useConform ? 1 : stretch * pitchRatio
      if (!useConform && rate !== 1) {
        src.playbackRate.value = rate
      }

      const gain = ctx.createGain()
      // Region length in real-world seconds (after playbackRate /
      // tempo-ratio scaling). Drives both the ADSR release timing
      // and the fallback `gateSec` when the caller doesn't specify
      // one. For the conform path the effective rate is the tempo
      // ratio (output_samples / input_samples inverted for "real"
      // duration).
      const effectiveRate = useConform
        ? (conformBpm ?? 1) / (sourceBpm ?? 1) / stretch
        : rate
      const bufferTail = buffer.duration - Math.max(0, sliceStart ?? 0)
      const regionLenSrc =
        sliceLenSec !== undefined && sliceLenSec > 0
          ? Math.min(sliceLenSec, bufferTail)
          : bufferTail
      const regionLenReal = regionLenSrc / Math.max(0.01, effectiveRate)

      if (envelope) {
        const env: SampleEnvelope = {
          a: envelope.a ?? DEFAULT_ENVELOPE.a,
          d: envelope.d ?? DEFAULT_ENVELOPE.d,
          s: envelope.s ?? DEFAULT_ENVELOPE.s,
          r: envelope.r ?? DEFAULT_ENVELOPE.r,
        }
        // Gate defaults to the natural play-through length so a hit
        // without an explicit note-off still gets a release tail.
        const gate = Math.max(0, gateSec ?? regionLenReal)
        const peakT = when + Math.max(0, env.a)
        const sustainT = peakT + Math.max(0, env.d)
        // Ensure the sustain window is non-negative — if `gate` is
        // shorter than A+D, skip the sustain step and start release
        // at sustainT (still monotonically rising `t` is required by
        // AudioParam automation).
        const releaseT = Math.max(sustainT, when + gate)
        const endT = releaseT + Math.max(0, env.r)
        gain.gain.setValueAtTime(0, when)
        gain.gain.linearRampToValueAtTime(velocity, peakT)
        gain.gain.linearRampToValueAtTime(velocity * env.s, sustainT)
        gain.gain.setValueAtTime(velocity * env.s, releaseT)
        gain.gain.linearRampToValueAtTime(0, endT)
      } else {
        gain.gain.value = velocity
      }
      // Signal chain: src → [rbNode] → gain → dest. When the
      // conform path is active the Rubber Band worklet sits
      // inline between the source and the ADSR gain; otherwise
      // it's a direct src→gain connection like before.
      if (rbNode) {
        src.connect(rbNode)
        rbNode.connect(gain)
      } else {
        src.connect(gain)
      }
      gain.connect(dest)

      // Release the worklet back to the pool when the source
      // finishes. BufferSourceNode fires `onended` both for
      // natural completion and for explicit .stop() — the pool
      // pattern is safe for both.
      if (rbNode) {
        const node = rbNode
        src.addEventListener(
          'ended',
          () => {
            releaseRbNode(node)
          },
          { once: true },
        )
      }

      if (
        sliceStart !== undefined &&
        sliceLenSec !== undefined &&
        sliceLenSec > 0
      ) {
        const safeStart = Math.max(0, Math.min(buffer.duration, sliceStart))
        const safeLen = Math.min(sliceLenSec, buffer.duration - safeStart)
        if (safeLen > 0) src.start(when, safeStart, safeLen)
      } else {
        src.start(when)
      }
    },
    [],
  )

  /**
   * Schedule one granular-pad hit. Builds a fresh Tone.GrainPlayer
   * per trigger (GrainPlayer holds internal state for grain
   * emission — sharing across overlapping hits isn't supported) and
   * tears it down after the release tail. Params map 1:1 to
   * GrainPlayer's native API with the exception of `grainSizeMs`,
   * which converts to Tone's seconds unit.
   *
   * Signal path: GrainPlayer → Tone.Gain (ADSR overlay, native
   * AudioNode) → `dest`. Because Tone shares our AudioContext
   * (see engine init), the Gain output plugs directly into the FX
   * bus input — no cross-context hops, no clock drift.
   *
   * Position / pitch spread are emulated: since GrainPlayer doesn't
   * expose a "jitter per grain" API, we schedule small `.update()`
   * bumps every `grainEmitSec = grainSizeMs × (1 − overlap)` until
   * the hit's gate ends. Keeps v1 spread audible without a DSP
   * rewrite; a proper spread implementation can come later.
   */
  const scheduleGranular = useCallback(
    (
      ctx: AudioContext,
      dest: AudioNode,
      buffer: AudioBuffer,
      when: number,
      velocity: number,
      pitchSemitones: number,
      padDef: {
        grainSizeMs: number
        overlap: number
        detuneCents: number
        playbackRate: number
        reverse: boolean
        positionSec: number
        positionSpread: number
        pitchSpread: number
        loop: boolean
      },
      timeStretch?: number,
      envelope?: Partial<SampleEnvelope>,
      gateSec?: number,
    ) => {
      const toneBuffer = new ToneAudioBuffer()
      toneBuffer.set(buffer)
      const player = new GrainPlayer(toneBuffer)

      const grainSec = Math.max(0.005, Math.min(2, padDef.grainSizeMs / 1000))
      player.grainSize = grainSec
      player.overlap = Math.max(0, Math.min(1, padDef.overlap))
      const baseDetune = Math.max(
        -2400,
        Math.min(2400, padDef.detuneCents + pitchSemitones * 100),
      )
      player.detune = baseDetune
      const rate = Math.max(
        0.25,
        Math.min(4, padDef.playbackRate * (timeStretch ?? 1)),
      )
      player.playbackRate = rate
      player.reverse = padDef.reverse
      player.loop = padDef.loop
      if (padDef.loop) {
        // Loop inside a small region around the start position so a
        // Freeze pad doesn't sweep the whole sample. Half-second
        // window by default — matches the ScrubPad's sustain loop.
        const loopWin = Math.min(0.5, buffer.duration)
        const ls = Math.max(0, padDef.positionSec)
        player.loopStart = Math.min(buffer.duration - 0.01, ls)
        player.loopEnd = Math.min(buffer.duration, ls + loopWin)
      }

      // ADSR overlay — same shape as scheduleSample so pad envelopes
      // behave identically across kinds.
      const gain = ctx.createGain()
      const env: SampleEnvelope = {
        a: envelope?.a ?? DEFAULT_ENVELOPE.a,
        d: envelope?.d ?? DEFAULT_ENVELOPE.d,
        s: envelope?.s ?? DEFAULT_ENVELOPE.s,
        r: envelope?.r ?? DEFAULT_ENVELOPE.r,
      }
      // Gate for granular defaults to 0.5s (a single Freeze hit) if
      // the caller didn't specify one. Sequencer cells always pass a
      // gateSec derived from step length.
      const gate = Math.max(0.05, gateSec ?? 0.5)
      const peakT = when + Math.max(0, env.a)
      const sustainT = peakT + Math.max(0, env.d)
      const releaseT = Math.max(sustainT, when + gate)
      const endT = releaseT + Math.max(0, env.r)
      gain.gain.setValueAtTime(0, when)
      gain.gain.linearRampToValueAtTime(velocity, peakT)
      gain.gain.linearRampToValueAtTime(velocity * env.s, sustainT)
      gain.gain.setValueAtTime(velocity * env.s, releaseT)
      gain.gain.linearRampToValueAtTime(0, endT)

      // Tone.GrainPlayer has an output gain — connect it into our
      // native gain, which then hits the FX bus.
      player.connect(gain)
      gain.connect(dest)

      // Start grain emission. GrainPlayer accepts time in seconds
      // relative to Tone's transport; since we share the AudioContext
      // with Tone, `when` (our ctx.currentTime-based timestamp) maps
      // directly.
      const startOffset = Math.max(
        0,
        Math.min(buffer.duration - 0.01, padDef.positionSec),
      )
      const playDur = endT - when
      try {
        player.start(when, startOffset, playDur)
      } catch {
        /* start-in-past on Safari — fall back to immediate start */
        try {
          player.start(undefined, startOffset, playDur)
        } catch {
          /* if GrainPlayer still refuses, dispose and bail */
          player.dispose()
          toneBuffer.dispose()
          return
        }
      }

      // Spread emulation: schedule per-grain updates while the hit
      // is sustaining. Bail out of the loop if spread is zero (no
      // work to do) or the grain rate is too fast to be useful.
      let spreadTimer: number | null = null
      if (padDef.positionSpread > 0 || padDef.pitchSpread > 0) {
        const emitSec = Math.max(0.02, grainSec * (1 - player.overlap))
        const startMs = Math.max(0, (when - ctx.currentTime) * 1000)
        const durMs = Math.max(0, (releaseT - when) * 1000)
        const stepMs = emitSec * 1000
        const originPos = startOffset
        const originDetune = baseDetune
        spreadTimer = window.setInterval(() => {
          try {
            if (padDef.positionSpread > 0) {
              const jitter =
                (Math.random() * 2 - 1) *
                padDef.positionSpread *
                buffer.duration
              const next = Math.max(
                0,
                Math.min(buffer.duration - 0.01, originPos + jitter),
              )
              player.loopStart = Math.min(buffer.duration - 0.01, next)
              player.loopEnd = Math.min(buffer.duration, next + 0.1)
            }
            if (padDef.pitchSpread > 0) {
              const jitter = (Math.random() * 2 - 1) * padDef.pitchSpread
              player.detune = Math.max(
                -2400,
                Math.min(2400, originDetune + jitter),
              )
            }
          } catch {
            /* player may have been disposed by the cleanup timer */
          }
        }, stepMs)
        // Clear the jitter loop when the hit finishes.
        window.setTimeout(
          () => {
            if (spreadTimer !== null) {
              window.clearInterval(spreadTimer)
              spreadTimer = null
            }
          },
          startMs + durMs + 50,
        )
      }

      // Cleanup. GrainPlayer doesn't auto-dispose after its duration
      // expires, so schedule a teardown past the release tail.
      window.setTimeout(
        () => {
          try {
            player.stop()
          } catch {
            /* already stopped */
          }
          try {
            player.dispose()
          } catch {
            /* already disposed */
          }
          try {
            toneBuffer.dispose()
          } catch {
            /* already disposed */
          }
          if (spreadTimer !== null) {
            window.clearInterval(spreadTimer)
            spreadTimer = null
          }
        },
        Math.max(50, (endT - ctx.currentTime) * 1000 + 100),
      )
    },
    [],
  )

  /**
   * Fire one voice at a precise future time. Connects to the master
   * FX bus so whatever effects are active at `when` apply; any
   * per-step FX sends in `opts.fx` are automated around the step.
   */
  const trigger = useCallback(
    (
      kitId: KitId,
      type: VoiceType | null,
      when: number,
      opts: TriggerOptions = {},
    ) => {
      const ctx = ctxRef.current ?? ensureContext()
      if (!ctx) return
      ctxRef.current = ctx
      const bus = ensureBus(ctx)
      // Resolve the voice's destination node via the bus registry.
      // `null` → no registry set up yet → fall back to master input.
      // User buses feed into master so per-step FX sends still hit
      // the master chain, just with per-bus volume/pan in between.
      const voiceDest = resolveBusInput(opts.busId) ?? bus.input
      const at = Math.max(when, ctx.currentTime)

      const velocity = Math.max(0.05, Math.min(1, opts.velocity ?? 0.9))
      const pitch = opts.pitchSemitones ?? 0

      // Schedule per-step FX automation BEFORE the voice trigger so
      // the wet gains are at their step values by the time the voice
      // output reaches them. `holdSec` of 0.12s is long enough to
      // catch the voice's attack + first tail without bleeding too
      // far into the next step at typical BPMs.
      if (opts.fx) {
        for (const [fxId, amount] of Object.entries(opts.fx) as Array<
          [FxId, number | undefined]
        >) {
          if (amount === undefined) continue
          bus.automateSend(fxId, at - 0.005, amount, 0.18)
        }
      }

      // Granular handling. Build or fetch the buffer, then route
      // through scheduleGranular. The dispatch branches before
      // sample / synth because granular pads always carry a
      // sampleId and never fall back to procedural voices — if the
      // buffer isn't cached we wait on the fetch and play late
      // (same pattern as sample cache-miss below).
      if (opts.granular) {
        const g = opts.granular
        const cached = sampleBufferCache.current.get(g.sampleId)
        if (cached) {
          scheduleGranular(
            ctx,
            voiceDest,
            cached,
            at,
            velocity,
            pitch,
            g,
            undefined,
            g.envelope,
            g.gateSec,
          )
          return
        }
        if (g.playUrl) {
          fetchSampleBuffer(ctx, g.sampleId, g.playUrl)
            .then(buf => {
              if (!buf) return
              if (at + 0.5 < ctx.currentTime) return
              scheduleGranular(
                ctx,
                voiceDest,
                buf,
                ctx.currentTime,
                velocity,
                pitch,
                g,
                undefined,
                g.envelope,
                g.gateSec,
              )
            })
            .catch(err => {
              console.warn(
                `[granular-miss] fetch failed for ${g.sampleId}:`,
                err,
              )
            })
        }
        return
      }

      // Sample handling. Three cases:
      //   (a) sample + cache hit → play the sample only.
      //   (b) sample + cache miss + `type` set → fall through to
      //       the procedural voice this hit (kit-voice-with-overlay
      //       semantics, preserved from v5), kick off a background
      //       fetch for next time.
      //   (c) sample + cache miss + `type` null → no-op this hit
      //       (pure-sample pad; nothing sensible to fall back to).
      //       Async fetch still runs so the next hit succeeds.
      if (opts.sample) {
        const {
          sampleId,
          playUrl,
          sliceStart,
          sliceLenSec,
          reverse,
          timeStretch,
          envelope,
          gateSec,
          sourceBpm,
          conformBpm,
        } = opts.sample
        const cached = sampleBufferCache.current.get(sampleId)
        if (cached) {
          // Reverse: swap in the reversed buffer and translate the
          // slice region into its time axis. A forward slice
          // `[startSec, endSec)` becomes `[duration-endSec,
          // duration-startSec)` in the reversed buffer — same bytes,
          // same length, just read back-to-front.
          let playBuffer = cached
          let playStart = sliceStart
          const playLen = sliceLenSec
          if (reverse) {
            playBuffer = getReversedBuffer(ctx, sampleId, cached)
            if (sliceStart !== undefined && sliceLenSec !== undefined) {
              playStart = cached.duration - (sliceStart + sliceLenSec)
            }
          }
          scheduleSample(
            ctx,
            voiceDest,
            playBuffer,
            at,
            velocity,
            pitch,
            playStart,
            playLen,
            timeStretch,
            envelope,
            gateSec,
            sourceBpm,
            conformBpm,
          )
          return
        }
        if (playUrl) {
          // Kick off the fetch AND register a late-play so this hit
          // isn't silently dropped. `fetchSampleBuffer` dedupes
          // concurrent fetches for the same sampleId, so spamming the
          // pad queues N late-plays against one network request.
          fetchSampleBuffer(ctx, sampleId, playUrl)
            .then(buf => {
              if (!buf) return
              // Only replay if the original schedule time is recent —
              // more than ~500ms in the past means we've drifted too
              // far for the hit to be musically useful (a scheduler
              // hit mid-pattern, say). For click hits `at` ≈
              // `currentTime`, so this never gates out. The replay
              // uses fresh `currentTime()` to avoid start-in-past
              // exceptions on Safari.
              if (at + 0.5 < ctx.currentTime) return
              let playBuffer = buf
              let playStart = sliceStart
              if (reverse) {
                playBuffer = getReversedBuffer(ctx, sampleId, buf)
                if (sliceStart !== undefined && sliceLenSec !== undefined) {
                  playStart = buf.duration - (sliceStart + sliceLenSec)
                }
              }
              scheduleSample(
                ctx,
                voiceDest,
                playBuffer,
                ctx.currentTime,
                velocity,
                pitch,
                playStart,
                sliceLenSec,
                timeStretch,
                envelope,
                gateSec,
                sourceBpm,
                conformBpm,
              )
            })
            .catch(err => {
              console.warn(`[sample-miss] fetch failed for ${sampleId}:`, err)
            })
        }
        if (type === null) {
          // Silent-fail surface: still fire the event so the toast
          // shows "loading…". The .then() above will actually play
          // the sample when the buffer lands; the toast just tells
          // the user why the first hit felt late.
          if (typeof window !== 'undefined') {
            try {
              window.dispatchEvent(
                new CustomEvent('busswerks:sample-miss', {
                  detail: { sampleId, hasPlayUrl: Boolean(playUrl) },
                }),
              )
            } catch {
              /* older browsers without CustomEvent support — ignore */
            }
          }
          return
        }
        // else fall through to procedural voice this hit
      }

      // Pure-MIDI or empty-content pads pass `type === null` without
      // a sample — nothing to synthesise, just return.
      if (type === null) return

      triggerVoice(
        ctx,
        voiceDest,
        kitId,
        type,
        at,
        velocity,
        pitch,
        opts.envelope,
        opts.gateSec,
      )
    },
    [fetchSampleBuffer, scheduleSample, scheduleGranular, getReversedBuffer],
  )

  /**
   * Eagerly load a sample into the cache — useful before playback
   * starts so the first hit isn't silent. Called by the sample
   * picker after selecting a new sample for a pad.
   */
  const preloadSample = useCallback(
    async (sampleId: string, playUrl: string): Promise<boolean> => {
      const ctx = ctxRef.current ?? ensureContext()
      if (!ctx) {
        console.warn(
          `[preload] no AudioContext available for ${sampleId}; will retry on first gesture`,
        )
        return false
      }
      ctxRef.current = ctx
      const buf = await fetchSampleBuffer(ctx, sampleId, playUrl)
      if (!buf) {
        console.warn(
          `[preload] fetch/decode failed for ${sampleId} — pad hits will be silent until this resolves`,
        )
      }
      return buf !== null
    },
    [fetchSampleBuffer],
  )

  /**
   * Engage or bypass a master-bus effect. `amount` (0..1) scales the
   * engaged target level — use it to track the FX Bank's per-effect
   * fader. Defaults to 1.0 so legacy callers keep working unchanged.
   */
  const setFx = useCallback((fxId: FxId, active: boolean, amount = 1) => {
    const ctx = ctxRef.current ?? ensureContext()
    if (!ctx) return
    ctxRef.current = ctx
    ensureBus(ctx).engage(fxId, active, amount)
  }, [])

  /**
   * Flip the performance-FX filter mode (1 = HP, 2 = LP). Propagates
   * immediately to the live master filter so drag-in-progress stays
   * musical — the cutoff sweep snaps to the new mode's range without
   * first passing through bypass.
   */
  const setPerfFilterMode = useCallback((mode: 1 | 2) => {
    const ctx = ctxRef.current ?? ensureContext()
    if (!ctx) return
    ctxRef.current = ctx
    ensureBus(ctx).setFilterMode(mode)
  }, [])

  /**
   * Fire an instrument plugin voice. Looks up `instrumentId` in the
   * plugin registry, builds a fresh voice, triggers it, and auto-
   * disposes after a short grace period so host callers don't
   * need to track voices themselves.
   *
   * Sampler instruments (`instrumentId === 'sampler'`, plus any
   * future buffer-backed instrument) need an AudioBuffer injected
   * into the preset at fire time. Callers pass the optional
   * `sample` handle; the engine looks the buffer up in the shared
   * preload cache (same cache `scheduleSample` uses), and merges
   * it into the preset before `createVoice`. A cache-miss fires
   * the usual late-play fetch → re-trigger path so the first hit
   * on a cold sample isn't silent.
   */
  const triggerInstrument = useCallback(
    (
      instrumentId: string,
      when: number,
      velocity: number,
      pitchSemitones: number,
      preset?: Record<string, number | string>,
      busId?: string,
      gateSec = 0.25,
      sample?: {
        sampleId: string
        playUrl?: string
        sliceStart?: number
        sliceLen?: number
      },
    ) => {
      const ctx = ctxRef.current ?? ensureContext()
      if (!ctx) return
      ctxRef.current = ctx
      const plugin = getInstrument(instrumentId)
      if (!plugin) return
      const bus = ensureBus(ctx)
      const dest = resolveBusInput(busId) ?? bus.input

      const fire = (resolvedPreset: Record<string, InstrumentPresetValue>) => {
        const voice = plugin.createVoice(ctx, resolvedPreset)
        try {
          voice.output.connect(dest)
        } catch {
          /* already connected or destination closed */
        }
        const at = Math.max(when, ctx.currentTime)
        voice.trigger(at, velocity, pitchSemitones)
        // Release after the gate window; actual dispose follows the
        // release tail (most voices resolve their envelope within
        // ~4 s of release). Longer-tail instruments should expose
        // their own explicit stop API; this is the MVP path.
        const releaseT = at + gateSec
        if (voice.release) voice.release(releaseT)
        const disposeT = releaseT + 4
        const disposeMs = Math.max(0, (disposeT - ctx.currentTime) * 1000)
        window.setTimeout(() => {
          try {
            voice.dispose()
          } catch {
            /* already disposed */
          }
        }, disposeMs)
      }

      // Buffer-backed instruments: resolve the AudioBuffer before
      // firing. Sampler is the first resident; future buffer-backed
      // instruments can opt in by declaring `preset.buffer` in their
      // paramSchema + checking it in `createVoice`.
      const needsBuffer = instrumentId === 'sampler' && sample?.sampleId
      if (needsBuffer) {
        const basePreset: Record<string, InstrumentPresetValue> = {
          ...(preset ?? {}),
        }
        if (typeof sample.sliceStart === 'number') {
          basePreset.sliceStart = sample.sliceStart
        }
        if (typeof sample.sliceLen === 'number') {
          basePreset.sliceLen = sample.sliceLen
        }
        const cached = sampleBufferCache.current.get(sample.sampleId)
        if (cached) {
          basePreset.buffer = cached
          fire(basePreset)
          return
        }
        if (sample.playUrl) {
          // Late-play: fetch the buffer, then re-fire relative to
          // the current clock. Mirrors the sample-path pattern —
          // skip if the original schedule time has drifted past the
          // half-second musical threshold.
          fetchSampleBuffer(ctx, sample.sampleId, sample.playUrl)
            .then(buf => {
              if (!buf) return
              if (when + 0.5 < ctx.currentTime) return
              basePreset.buffer = buf
              const latePreset = { ...basePreset }
              const laterVoice = plugin.createVoice(ctx, latePreset)
              try {
                laterVoice.output.connect(dest)
              } catch {
                /* already connected */
              }
              const at = ctx.currentTime
              laterVoice.trigger(at, velocity, pitchSemitones)
              const releaseT = at + gateSec
              if (laterVoice.release) laterVoice.release(releaseT)
              const disposeMs = Math.max(0, (releaseT + 4 - at) * 1000)
              window.setTimeout(() => {
                try {
                  laterVoice.dispose()
                } catch {
                  /* already disposed */
                }
              }, disposeMs)
            })
            .catch(err => {
              console.warn(
                `[sampler-miss] fetch failed for ${sample.sampleId}:`,
                err,
              )
            })
        }
        return
      }

      fire((preset ?? {}) as Record<string, InstrumentPresetValue>)
    },
    [fetchSampleBuffer],
  )

  const currentTime = useCallback(() => {
    return ctxRef.current?.currentTime ?? 0
  }, [])

  /**
   * Start a sustained instrument voice. Returns an opaque token that
   * the caller holds and passes to `releaseInstrument` when the key
   * comes up. Unlike `triggerInstrument`, this does NOT schedule a
   * release or dispose — the voice keeps ringing until either the
   * caller releases it or the 30 s safety deadline fires.
   *
   * Returns null if:
   *   - The instrument isn't registered.
   *   - Buffer-backed instruments (sampler) are requested — attack /
   *     release is instrument-plugin-only in this iteration. Use
   *     `triggerInstrument` for samplers; their envelope semantics
   *     are the slice-length, not a key-hold.
   */
  const attackInstrument = useCallback(
    (
      instrumentId: string,
      velocity: number,
      pitchSemitones: number,
      preset?: Record<string, InstrumentPresetValue>,
      busId?: string,
      when?: number,
    ): number | null => {
      const ctx = ctxRef.current ?? ensureContext()
      if (!ctx) return null
      ctxRef.current = ctx
      const plugin = getInstrument(instrumentId)
      if (!plugin) return null
      // Sampler is buffer-backed and doesn't match the held-note model
      // (its "sustain" is its slice length). Callers use
      // triggerInstrument for samplers.
      if (instrumentId === 'sampler') return null

      const bus = ensureBus(ctx)
      const dest = resolveBusInput(busId) ?? bus.input
      const voice = plugin.createVoice(ctx, preset ?? {})
      try {
        voice.output.connect(dest)
      } catch {
        /* already connected or destination closed */
      }
      const at = Math.max(when ?? ctx.currentTime, ctx.currentTime)
      voice.trigger(at, velocity, pitchSemitones)

      voiceTokenCounterRef.current += 1
      const token = voiceTokenCounterRef.current

      // Safety deadline — force release 30 s after attack so a missed
      // pointerup / tab-switch can't leave a note ringing forever.
      const forceReleaseMs = 30_000
      const forceReleaseTimeout = window.setTimeout(() => {
        releaseInstrumentInternal(token)
      }, forceReleaseMs)

      liveVoicesRef.current.set(token, {
        voice,
        ctx,
        released: false,
        forceReleaseTimeout,
      })
      return token
    },
    [],
  )

  /**
   * Internal release — used by both the public callback and the
   * safety-net timeout. Idempotent per token (releasing twice is a
   * no-op). Schedules the voice's .release() + a dispose after the
   * release tail clears.
   */
  const releaseInstrumentInternal = useCallback(
    (token: number, when?: number): void => {
      const entry = liveVoicesRef.current.get(token)
      if (!entry || entry.released) return
      entry.released = true
      if (entry.forceReleaseTimeout !== null) {
        window.clearTimeout(entry.forceReleaseTimeout)
        entry.forceReleaseTimeout = null
      }
      const ctx = entry.ctx
      const releaseT = Math.max(when ?? ctx.currentTime, ctx.currentTime)
      try {
        if (entry.voice.release) entry.voice.release(releaseT)
      } catch {
        /* voice already disposed / rejected */
      }
      // Dispose after the release tail. 4 s is our house convention
      // (matches triggerInstrument). Covers any instrument whose
      // release envelope is ≤ 4 s, which is every first-party one.
      const disposeT = releaseT + 4
      const disposeMs = Math.max(0, (disposeT - ctx.currentTime) * 1000)
      window.setTimeout(() => {
        try {
          entry.voice.dispose()
        } catch {
          /* already disposed */
        }
        liveVoicesRef.current.delete(token)
      }, disposeMs)
    },
    [],
  )

  const releaseInstrument = useCallback(
    (token: number, when?: number): void => {
      releaseInstrumentInternal(token, when)
    },
    [releaseInstrumentInternal],
  )

  /**
   * Release every live voice — "panic". Wired to page-visibility
   * changes so tab-switch doesn't leave keys stuck. Callers can also
   * invoke it manually (e.g. a panic button in the header).
   */
  const releaseAllInstruments = useCallback((): void => {
    for (const token of Array.from(liveVoicesRef.current.keys())) {
      releaseInstrumentInternal(token)
    }
  }, [releaseInstrumentInternal])

  // Auto-panic on tab hide. Single effect — idempotent on re-mount.
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        releaseAllInstruments()
      }
    }
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [releaseAllInstruments])

  return {
    resume,
    trigger,
    triggerInstrument,
    attackInstrument,
    releaseInstrument,
    releaseAllInstruments,
    setFx,
    setPerfFilterMode,
    currentTime,
    preloadSample,
  }
}
